import { formatPropertyAddress, formatStreetAddress } from '../utils/propertyAddress';
import './MobileMapDisclosure.css';
import SearchFilterPanel from './SearchFilterPanel';
import {SavedPropertyLists,PropertyListPicker,PropertyListMembership} from './PropertyLists';
import MapPlaceLayers from './MapPlaceLayers';
import MapPropertyGrid from './MapPropertyGrid';
import {clusterBubbleIcon,propertyMapIcon} from '../utils/mapMarkerIcons';
import {addIllustratedBasemap} from '../services/illustratedBasemap';
import { propertyFetch } from '../services/idxPropertyClient';
import { loadPropertyMedia, propertyMediaUrls } from '../services/propertyMedia';
import { rememberPropertyPhoto } from '../services/propertyThumbnails';
import PropertyThumbnail from './PropertyThumbnail';
import PropertySwipeHint from './PropertySwipeHint';
import { writeSaveStorage } from '../services/propertySaveStorage';
import { usePropertySave } from './PropertySaveProvider';
import PropertyLocationMap from './PropertyLocationMap';
import {loadPropertyPage,loadPropertyMap,loadPrivateMapRows} from '../utils/boundedPropertySearch';
import {attachBoundedMap,renderBoundedFeatures,viewportBounds} from '../utils/boundedMapLayer';
import './BoundedMap.css';
import './PropertySearchLayout.css';
import RobustSearchControls from './RobustSearchControls';
import { searchSnapshot, searchQuery, searchFromQuery, assertValidSearch, readSearchError, ROBUST_NAMES } from '../utils/propertySearchFilters';
import { applyManualSearchFilters } from '../utils/manualSearchFilters';
// src/components/ActionButtons.js
import React, { useState, useEffect, useRef } from 'react';
import { chatSearchSnapshot, loadChatProperties, mergeChatProperties } from '../utils/chatPropertySearch';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import { SITE_CONFIG, SEO_CONFIG } from '../config';
import { decryptPassword } from '../utils/decrypt';
import { hasAdminAccess } from '../utils/authUser';
import { requestPasswordReset, signIn, signUp } from '../services/authService';
import {
  completeAnalyticsAuthentication,
  getAnalyticsIdentityForAuth,
  trackPropertySearch
} from '../services/analyticsService';
import { submitChatLead } from '../services/chatLeadService';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import AlertPill from './AlertPill';
import PropertyDetailModal from './PropertyDetailModal';
import ChatComposer from './ChatComposer';
import LanguageToggle from './LanguageToggle';
import MlsDisclosure from './MlsDisclosure';
import './ActionButtons.css';
import './MobilePropertyNavigation.css';
import './MapDarkMode.css';
import HoneypotField, { isHoneypotSubmission } from './HoneypotField';
import './MapControls.css';

// FLEET_FRONTEND_PACKAGE_2026
const POPULAR_SEARCH_AREAS = Object.keys(SITE_CONFIG.LANDING_PAGE?.CITY_IMAGES || {})
  .slice(0, 6)
  .map((key) => key.toLowerCase().split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' '));

const ActionButtons = ({ activeSection, setActiveSection, isGlobeMap, onGlobeMapClose, onGlobeMapResultsChange, directListingKey, initialCity, initialLogin = false, user, isAdmin, setUser, setIsAdmin, hidePrompts, closeDetailRef, isFullPage = false, onBack }) => {
  const assistantPropertyRailRef = useRef(null);
  const pendingPropertyRevealRef = useRef(null);
  const { favorites, collections, toggleFavorite } = usePropertySave();

  const { isDarkMode } = useTheme();
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const tr = (english, spanish) => isSpanish ? spanish : english;
  const navigate = useNavigate();

  const [mapStats,setMapStats]=useState({});
  const [lensMapData,setLensMapData]=useState(null);
  const initialMapRequestRef=useRef(0), initialMapAbortRef=useRef(null),lensAbortRef=useRef(null),chatAbortRef=useRef(null);
  const moreRequestsRef=useRef(new Map()),mapPreviewRequestsRef=useRef(new Map());
  useEffect(()=>()=>{initialMapRequestRef.current++;initialMapAbortRef.current?.abort();lensAbortRef.current?.abort();chatAbortRef.current?.abort();moreRequestsRef.current.forEach(controller=>controller.abort());chatGenerationRef.current++;},[]);

  const sharedSearchRef = useRef(searchFromQuery(window.location.search));
  const [advancedFilters, setAdvancedFilters] = useState(() => Object.fromEntries(Object.entries(sharedSearchRef.current).filter(([key]) => [...ROBUST_NAMES, 'minGarageSpaces', 'newConstruction', 'hasPrivatePool', 'waterfront', 'hasBasement', 'hasAttachedGarage', 'state'].includes(key))));
  const [manualSearchError, setManualSearchError] = useState('');
  const appliedSearchRef = useRef(sharedSearchRef.current);
  const applyManualFilters = query => {
    for(const [key,value] of Object.entries({listingKey,minBathrooms,maxBathrooms,hasPool:hasPool||undefined})) {query.delete(key);if(value!==''&&value!==undefined)query.set(key,String(value));}
    query.delete('communityFeatures');communityFeatures.forEach(value=>query.append('communityFeatures',value));
    if(acreage){query.delete('minLotSizeAcres');query.delete('maxLotSizeAcres');if(acreage==='<5')query.set('maxLotSizeAcres','5');else if(acreage==='5to10'){query.set('minLotSizeAcres','5');query.set('maxLotSizeAcres','10');}else if(acreage==='10+')query.set('minLotSizeAcres','10');}
    if (SITE_CONFIG.SEARCH_STATES && !query.has('state') && !query.has('listOfficeKeyNumeric')) {
      SITE_CONFIG.SEARCH_STATES.forEach(state => query.append('state', state));
    }
    applyManualSearchFilters(query, advancedFilters, sharedSearchRef.current);
    sharedSearchRef.current = null;
    setManualSearchError('');
  };

  const localFavoritesKey = `propertyFavorites_${SITE_CONFIG.SITE_ID}`;
  const [showPrompts, setShowPrompts] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [serviceStep, setServiceStep] = useState(0);
  const [isThinking, setIsThinking] = useState(false);
  const [isPropertySearching, setIsPropertySearching] = useState(false);
  const [isGlobeMapLoading, setIsGlobeMapLoading] = useState(false);
  const [propertyLocation, setPropertyLocation] = useState('');
  const [propertySize, setPropertySize] = useState('');
  const [propertyCondition, setPropertyCondition] = useState('');
  const [sellingTimeline, setSellingTimeline] = useState('');
  const [showThankYouMessage, setShowThankYouMessage] = useState(false);
  const [zipCode, setZipCode] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [messages, setMessages] = useState([]);
  const messagesContainerRef = useRef(null);
  const chatSearchContextRef = useRef(null);
  const chatGenerationRef = useRef(0);
  const chatRequestInFlightRef = useRef(false);
  const chatPropertyRequestRef = useRef(0);
  const chatSiteId = SITE_CONFIG.SITE_ID;

  useEffect(() => {
    chatSearchContextRef.current = null;
    chatRequestInFlightRef.current = false;
    setMessages([]);
    setIsThinking(false);
    setIsPropertySearching(false);
    return () => {
      chatGenerationRef.current += 1;
      chatPropertyRequestRef.current += 1;
    };
  }, [chatSiteId]);
  const [showRegistration, setShowRegistration] = useState(false);
  const [regEmail, setRegEmail] = useState('');
  const [regName, setRegName] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPhoneNumber, setRegPhoneNumber] = useState('');
  const [regFullName, setRegFullName] = useState('');
  const [showLogin, setShowLogin] = useState(initialLogin);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [expandedMap, setExpandedMap] = useState(null);
  const [placesExpandedMapInstance,setPlacesExpandedMapInstance]=useState(null);
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const [locationInput, setLocationInput] = useState('');
  const [globeMap, setGlobeMap] = useState(() => {
    const saved = sessionStorage.getItem('globeMapData');
    if (saved && isGlobeMap && new URLSearchParams(window.location.search).get('saved')==='true') {
      sessionStorage.removeItem('globeMapData');
      try { return JSON.parse(saved); } catch { return null; }
    }
    return null;
  });

  useEffect(() => {
    if (onGlobeMapResultsChange) {
      onGlobeMapResultsChange(Boolean(globeMap));
    }
  }, [globeMap, onGlobeMapResultsChange]);

  const globeMapRef = useRef(null);
  const globeLeafletRef = useRef(null);
  const globeMarkersRef = useRef(null);
  const [propertySubType, setPropertySubType] = useState('');
  const [minBedrooms, setMinBedrooms] = useState('');
  const [maxBedrooms, setMaxBedrooms] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [propertyDetail, setPropertyDetail] = useState(null);
  const [propertyImages, setPropertyImages] = useState({});
  const propertyImagesRef = useRef(propertyImages);
  const [propertyDetailImages, setPropertyDetailImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const detailRequestRef = useRef(0);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [showAgentListings, setShowAgentListings] = useState(false);
  const [propertyType, setPropertyType] = useState('Residential');
  const [likedProperties, setLikedProperties] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(localFavoritesKey) || '[]');
      return new Set(Array.isArray(saved) ? saved : []);
    } catch (error) {
      return new Set();
    }
  });
  useEffect(() => { setLikedProperties?.(favorites); }, [favorites, setLikedProperties]);

  const [savedHomesLoading, setSavedHomesLoading] = useState(false);
  const [savedHomesError, setSavedHomesError] = useState('');
  const [hasMedia, setHasMedia] = useState(false);
  const [minSqft, setMinSqft] = useState('');
  const [maxSqft, setMaxSqft] = useState('');
  const [stories, setStories] = useState('');
  const [minYearBuilt, setMinYearBuilt] = useState('');
  const [maxYearBuilt, setMaxYearBuilt] = useState('');
  const [listingKey, setListingKey] = useState('');
  const [hasFence, setHasFence] = useState(false);
  const [hasPool, setHasPool] = useState(false);
  const [foundation, setFoundation] = useState('');
  const [schoolDistrict, setSchoolDistrict] = useState('');
  const [communityFeatures, setCommunityFeatures] = useState([]);
  const [acreage, setAcreage] = useState('');
  const [minBathrooms, setMinBathrooms] = useState('');
  const [maxBathrooms, setMaxBathrooms] = useState('');
  const [priceSort, setPriceSort] = useState('');
  const [daysOnMarketSort] = useState('');
  const [listingDateSort, setListingDateSort] = useState('');
  const [mapView, setMapView] = useState(true);
  const [hybridView,setHybridView]=useState(true);
  const [mapTrayExpanded,setMapTrayExpanded]=useState(false);
  const [hybridMapInstance,setHybridMapInstance]=useState(null);
  const [activeMapTool,setActiveMapTool]=React.useState(null);
  const setPlacesOpen=React.useCallback(open=>setActiveMapTool(open?'places':null),[]);
  const setAlertsOpen=React.useCallback(open=>setActiveMapTool(open?'alerts':null),[]);
  React.useEffect(()=>setActiveMapTool(null),[propertyDetail,expandedMap,mapView]);
  const [listViewPage, setListViewPage] = useState(0);
  const itemsPerPage = 6;
  const [openChat, setOpenChat] = useState(false);
  const [pendingSellerParams, setPendingSellerParams] = useState(null);
  const [pendingBuyerParams, setPendingBuyerParams] = useState(null);
  const [chatLeadSubmitted, setChatLeadSubmitted] = useState(false);
  // Signature of the last lead submitted from conversational contact params, so a model
  // that repeats the same params across turns doesn't re-post the lead.
  const lastSubmittedLeadRef = useRef(null);
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [showAlertPill, setShowAlertPill] = useState(false);
  const [alertPillState, setAlertPillState] = useState('prompt');
  const alertTriggeredRef = useRef(false);
  const [senderEmail, setSenderEmail] = useState('');
  const [senderPassword, setSenderPassword] = useState('');
  const [searchLensEnabled, setSearchLensEnabled] = useState(false);
  const [searchLensProperties, setSearchLensProperties] = useState([]);
  const [searchLensLoading, setSearchLensLoading] = useState(false);
  const [searchLensResultCount, setSearchLensResultCount] = useState(0);
  const [searchLensRadiusMiles, setSearchLensRadiusMiles] = useState(null);
  const [searchLensPosition, setSearchLensPosition] = useState(null);
  const [searchLensSize, setSearchLensSize] = useState('medium');
  const [searchLensDragging, setSearchLensDragging] = useState(false);
  const [searchLensPreview, setSearchLensPreview] = useState(null);
  const [searchLensControlsOpen, setSearchLensControlsOpen] = useState(false);
  const [initialCityLoading, setInitialCityLoading] = useState(!!initialCity);
  const [mobileWorkspaceView, setMobileWorkspaceView] = useState('chat');
  const searchLensPositionRef = useRef(null);
  const searchLensDragRef = useRef(null);
  const searchLensDebounceRef = useRef(null);
  const searchLensRequestRef = useRef(0);
  const searchLensParamsRef = useRef(null);
  const lastTrackedSearchLensRef = useRef('');
  const assistantWorkspaceRef = useRef(null);
  const assistantResultsPanelRef = useRef(null);

  const scrollMobileWorkspaceTo = (view, behavior = 'smooth') => {
    setMobileWorkspaceView(view);
    if (view === 'chat') pendingPropertyRevealRef.current = null;
    const workspace = assistantWorkspaceRef.current;
    if (!workspace) return;
    workspace.scrollTo({
      left: view === 'results' ? Math.max(0, workspace.scrollWidth - workspace.clientWidth) : 0,
      behavior
    });
  };

  const handleMobileWorkspaceScroll = () => {
    if (!window.matchMedia('(max-width: 820px)').matches) return;
    const workspace = assistantWorkspaceRef.current;
    if (!workspace) return;
    const nextView = workspace.scrollLeft > workspace.clientWidth / 2 ? 'results' : 'chat';
    setMobileWorkspaceView((currentView) => currentView === nextView ? currentView : nextView);
  };

  useEffect(() => { propertyImagesRef.current = propertyImages; }, [propertyImages]);
  useEffect(() => { searchLensPositionRef.current = searchLensPosition; }, [searchLensPosition]);

  useEffect(() => {
    const savedFilters = globeMap?.searchFilters;
    if (!savedFilters) return;

    setLocationInput(savedFilters.location || globeMap.location || '');
    setPropertyType(savedFilters.propertyType || 'Residential');
    setPropertySubType(savedFilters.propertySubType || '');
    setMinBedrooms(savedFilters.minBedrooms || '');
    setMaxBedrooms(savedFilters.maxBedrooms || '');
    setMinPrice(savedFilters.minPrice || '');
    setMaxPrice(savedFilters.maxPrice || '');
    setMinSqft(savedFilters.minSqft || '');
    setMaxSqft(savedFilters.maxSqft || '');
    setStories(savedFilters.stories || '');
    setMinYearBuilt(savedFilters.minYearBuilt || '');
    setMaxYearBuilt(savedFilters.maxYearBuilt || '');
    setListingKey(savedFilters.listingKey || '');
    setHasFence(Boolean(savedFilters.hasFence));
    setHasPool(Boolean(savedFilters.hasPool));
    setFoundation(savedFilters.foundation || '');
    setSchoolDistrict(savedFilters.schoolDistrict || '');
    setCommunityFeatures(Array.isArray(savedFilters.communityFeatures) ? savedFilters.communityFeatures : []);
    setAcreage(savedFilters.acreage || '');
    setMinBathrooms(savedFilters.minBathrooms || '');
    setMaxBathrooms(savedFilters.maxBathrooms || '');
    setPriceSort(savedFilters.priceSort || '');
    setListingDateSort(savedFilters.listingDateSort || '');
    setShowAgentListings(Boolean(savedFilters.showAgentListings));
    setHasMedia(Boolean(savedFilters.hasMedia));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- hydrate only the result set restored from session storage

  useEffect(() => {
    if (closeDetailRef) {
      closeDetailRef.current = () => {
        if (enlargedImage) { setEnlargedImage(null); return true; }
        if (propertyDetail) { setPropertyDetail(null); return true; }
        return false;
      };
    }
  }, [closeDetailRef, propertyDetail, enlargedImage]);

  useEffect(() => {
    let cancelled = false;

    try {
      const cached = JSON.parse(localStorage.getItem(`featureFlags_${SITE_CONFIG.SITE_ID}`) || 'null');
      if (cached && typeof cached.openChat === 'boolean') setOpenChat(cached.openChat);
    } catch (e) { /* ignore */ }

    const fetchFeatureFlags = async (attempt = 0) => {
      const retry = () => {
        if (attempt < 3 && !cancelled) {
          setTimeout(() => fetchFeatureFlags(attempt + 1), 1500 * (attempt + 1));
          return true;
        }
        return false;
      };
      try {
        const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/feature-flag?siteId=${SITE_CONFIG.SITE_ID}`, {
          headers: { 'X-API-Key': 'treas-api-key-2025' }
        });
        if ((response.status === 429 || response.status >= 500) && retry()) return;
        if (response.ok) {
          const data = await response.json();
          if (cancelled) return;
          setOpenChat(data.openChat || false);
          if (data.senderEmail) setSenderEmail(data.senderEmail);
          if (data.senderEmailPassword) {
            decryptPassword(data.senderEmailPassword).then(pw => { if (pw) setSenderPassword(pw); });
          }
          try {
            localStorage.setItem(`featureFlags_${SITE_CONFIG.SITE_ID}`, JSON.stringify({ openChat: !!data.openChat }));
          } catch (e) { /* ignore */ }
        }
      } catch (error) {
        if (retry()) return;
        console.error('Error fetching feature flags:', error);
      }
    };
    fetchFeatureFlags();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('leadInfo');
    if (saved) {
      const { name: n, email: e, phone: p } = JSON.parse(saved);
      if (n) setName(n);
      if (e) setEmail(e);
      if (p) setPhoneNumber(p);
      if (n && e && p) setChatLeadSubmitted(true);
    }
  }, []);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (showRegistration || showLogin || selectedService || isThinking || isPropertySearching) {
      setTimeout(scrollToBottom, 200);
    }
  }, [showRegistration, showLogin, selectedService, serviceStep, isThinking, isPropertySearching]);

  // Handle direct listing access
  useEffect(() => {
    if (directListingKey && isGlobeMap) {
      fetchPropertyDetailByListingKey(directListingKey);
      sessionStorage.removeItem('directListingKey');
    }
  }, [directListingKey, isGlobeMap]); // eslint-disable-line react-hooks/exhaustive-deps -- fetch helper is intentionally triggered only by route inputs

  // Auto-run search from URL params (shareable search links)
  useEffect(() => {
    if (!isGlobeMap) return;
    const params = new URLSearchParams(window.location.search);
    const legacyQuery = params.get('q');
    const searchLocation = params.get('location') || legacyQuery || params.get('city') || params.get('zip') || params.get('state');
    const isSearchLensLink = params.get('searchLens') === 'true';
    if (!searchLocation && !isSearchLensLink) return;
    // Keep old shared q= links working, but use location= going forward so
    // GA Enhanced Measurement does not also auto-fire a site-search event.
    if (legacyQuery && !params.has('location')) {
      params.set('location', legacyQuery);
      params.delete('q');
      window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
    }
    // Set filter state from URL params
    if (params.get('propertyType')) setPropertyType(params.get('propertyType'));
    if (params.get('propertySubType')) setPropertySubType(params.get('propertySubType'));
    if (params.get('minBedrooms')) setMinBedrooms(params.get('minBedrooms'));
    if (params.get('maxBedrooms')) setMaxBedrooms(params.get('maxBedrooms'));
    if (params.get('minPrice')) setMinPrice(params.get('minPrice'));
    if (params.get('maxPrice')) setMaxPrice(params.get('maxPrice'));
    if (params.get('minSqft')) setMinSqft(params.get('minSqft'));
    if (params.get('maxSqft')) setMaxSqft(params.get('maxSqft'));
    if (params.get('stories')) setStories(params.get('stories'));
    if (params.get('minYearBuilt')) setMinYearBuilt(params.get('minYearBuilt'));
    if (params.get('maxYearBuilt')) setMaxYearBuilt(params.get('maxYearBuilt'));
    if (params.get('listingKey')) setListingKey(params.get('listingKey'));
    if (params.get('hasFence') === 'true') setHasFence(true);
    if (params.get('foundation')) setFoundation(params.get('foundation'));
    if (params.get('schoolDistrict')) setSchoolDistrict(params.get('schoolDistrict'));
    if (params.get('priceSort')) setPriceSort(params.get('priceSort'));
    if (params.get('hasMedia') === 'true') setHasMedia(true);
    if (isSearchLensLink) setSearchLensEnabled(true);
    setLocationInput(searchLocation || '');
    if (params.get('view') === 'filters') return;
    fetchPropertiesForGlobe(searchLocation || '', isSearchLensLink);
  }, [isGlobeMap]); // eslint-disable-line react-hooks/exhaustive-deps -- URL hydration runs only when map mode opens

  // Auto-search when initialCity is provided from landing page dropdown
  useEffect(() => {
    if (initialCity && initialCityLoading) {
      setLocationInput(initialCity);
      fetchPropertiesForGlobe(initialCity).then(() => setInitialCityLoading(false)).catch(() => setInitialCityLoading(false));
    }
  }, [initialCity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (expandedMap && mapRef.current) {
      // Clean up existing map
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
      }

      // Create new map
      const map = L.map(mapRef.current,{minZoom:1,maxZoom:19}).setView([expandedMap.lat, expandedMap.lon], 15);

      addIllustratedBasemap(map);

      // Add house marker
      const houseIcon = L.divIcon({
        html: '🏠',
        className: 'house-marker',
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });

      L.marker([expandedMap.lat, expandedMap.lon], { icon: houseIcon }).addTo(map);

      leafletMapRef.current = map;
      setPlacesExpandedMapInstance(map);
    }

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        setPlacesExpandedMapInstance(null);
      }
    };
  }, [expandedMap]);

  const commonPrompts = [
    { display: zipCode ? tr(`Homes in ${zipCode}`, `Casas en ${zipCode}`) : tr('Find a home', 'Buscar una casa'), value: zipCode ? tr(`Show me properties in ${zipCode}`, `Muéstrame propiedades en ${zipCode}`) : tr('Show me properties in Atlanta', 'Muéstrame propiedades en Atlanta') },
    { display: tr('Create an alert', 'Crear una alerta'), value: tr('Help me create a property alert', 'Ayúdame a crear una alerta de propiedades') },
    { display: tr('Sell my home', 'Vender mi casa'), value: tr('I am thinking about selling my home', 'Estoy pensando en vender mi casa') },
    { display: tr(`Contact ${SITE_CONFIG.AGENT.NAME}`, `Contactar a ${SITE_CONFIG.AGENT.NAME}`), value: tr(`Contact ${SITE_CONFIG.AGENT.NAME}`, `Quiero contactar a ${SITE_CONFIG.AGENT.NAME}`) }
  ];

  const handlePromptSelect = (promptValue) => {
    setInputValue(promptValue);
    setShowPrompts(false);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = isHoneypotSubmission(e.currentTarget) ? { ok: true } : await submitChatLead({
        postalCode: zipCode,
        name,
        phoneNumber,
        serviceReq: selectedService,
        email,
        siteId: SITE_CONFIG.SITE_ID
      }, { rememberContact: true });

      if (response.ok) {
        console.log('Form submitted successfully');
        // Clear form but keep customer info for later use
        // setZipCode(''); // Keep zip code for property search
        // Keep name, phone, email for selling details form
        // setName('');
        // setPhoneNumber('');
        // setEmail('');
        // Show thank you message
        setShowThankYouMessage(true);
        setTimeout(scrollToBottom, 200);
      } else {
        console.error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || chatRequestInFlightRef.current || isPropertySearching) return;

    // Add user message
    const userMessage = { type: 'user', content: inputValue };
    setMessages(prev => [...prev, userMessage]);

    const messageToSend = inputValue;
    const lowerMessage = inputValue.toLowerCase();
    setInputValue('');

    // Check for sign in keywords
    if (lowerMessage.includes('sign in') || lowerMessage.includes('signin') || lowerMessage.includes('login') || lowerMessage.includes('log in') || lowerMessage.includes('iniciar sesión') || lowerMessage.includes('inicia sesión')) {
      setShowLogin(true);
      const botMessage = { type: 'bot', content: tr('I\'ll help you sign in. Please enter your credentials below:', 'Le ayudaré a iniciar sesión. Ingrese sus datos a continuación:') };
      setMessages(prev => [...prev, botMessage]);
      return;
    }

    chatRequestInFlightRef.current = true;
    const generation = chatGenerationRef.current;
    setIsThinking(true);
    try {
      const response = await fetch('https://tophamtechbe.com/llm/v1/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: messageToSend,
          site_id: SITE_CONFIG.SITE_ID,
          language,
          // Opt in to conversational contact capture (backend collects name/phone/email
          // in chat and returns them on seller_inquiry params). Older backends ignore this.
          capabilities: ['conversational_contact', 'robust_search_v1'],
          // Tell the bot not to re-ask for contact info we already hold.
          known_lead: !!(user || chatLeadSubmitted),
          ...(chatSearchContextRef.current?.siteId === chatSiteId
            ? { search_context: chatSearchContextRef.current.params } : {}),
          conversation_history: messages.filter(msg => msg.content !== 'properties').map(msg => ({
            role: msg.type === 'user' ? 'user' : 'assistant',
            content: msg.content
          }))
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (generation !== chatGenerationRef.current) return;
        // Add bot response
        const botMessage = { type: 'bot', content: data.response };
        setMessages(prev => [...prev, botMessage]);

        // Handle action if present
        if (data.action) {
          console.log('Action received:', data.action, data.params);
          await handleAction(data.action, data.params);
        }
      } else {
        if (generation !== chatGenerationRef.current) return;
        const errorMessage = { type: 'bot', content: tr('Sorry, I\'m having trouble connecting right now. Please try again later.', 'Lo siento, tengo problemas para conectarme en este momento. Inténtelo de nuevo más tarde.') };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      if (generation !== chatGenerationRef.current) return;
      console.error('Error sending message:', error);
      const errorMessage = { type: 'bot', content: tr('Sorry, I\'m having trouble connecting right now. Please try again later.', 'Lo siento, tengo problemas para conectarme en este momento. Inténtelo de nuevo más tarde.') };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      if (generation === chatGenerationRef.current) {
        chatRequestInFlightRef.current = false;
        setIsThinking(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage(e);
    }
  };

  const submitSellerInquiry = async (params, userEmail, userName, userPhone) => {
    try {
      const p = params || {};
      const queryParams = new URLSearchParams({
        location: p.location || '',
        propertyDetails: p.property_details || '',
        email: userEmail,
        phoneNumber: userPhone,
        name: userName,
        siteId: SITE_CONFIG.SITE_ID
      });

      if (p.address) {
        queryParams.append('address', p.address);
      }

      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/selling-inquiry?${queryParams}`, {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        console.log('Seller inquiry submitted successfully');
      } else {
        console.error('Failed to submit seller inquiry');
      }
    } catch (error) {
      console.error('Error submitting seller inquiry:', error);
    }
  };

  const storeConversation = async (params) => {
    try {
      const queryParams = new URLSearchParams({
        reason: params.reason || 'inquiry',
        contactInfo: email || params.contactInfo || '',
        conversationSummary: params.conversationSummary || '',
        phone: phoneNumber || params.phone || '',
        name: name || params.name || '',
        siteId: SITE_CONFIG.SITE_ID
      });

      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/store-convo?${queryParams}`, {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        console.log('Conversation stored successfully');
      } else {
        console.error('Failed to store conversation');
      }
    } catch (error) {
      console.error('Error storing conversation:', error);
    }
  };

  const sendAgentSMS = async (customerName, customerPhone) => {
    try {
      const formData = new FormData();
      formData.append('to', SITE_CONFIG.AGENT.PHONE);
      formData.append('body', `Agent Winds Message: ${customerName} has requested you contact them at ${customerPhone}`);

      const response = await fetch('https://tophamtechbe.com/api/treas/comm/send-sms', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        },
        body: formData
      });

      if (response.ok) {
        console.log('SMS sent to agent successfully');
      } else {
        console.error('Failed to send SMS to agent');
      }
    } catch (error) {
      console.error('Error sending SMS to agent:', error);
    }
  };

  const handleGetCRM = async () => {
    const userEmail = user?.email || email;
    if (!userEmail) {
      const botMessage = { type: 'bot', content: tr('Please sign in to view your progress status.', 'Inicie sesión para ver el estado de su proceso.') };
      setMessages(prev => [...prev, botMessage]);
      return;
    }

    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/crm?email=${encodeURIComponent(userEmail)}&siteId=${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const stages = ['lead', 'discover', 'onboard', 'research', 'offer', 'inspection', 'closing', 'complete'];
        const stageLabels = {
          lead: tr('Initial Lead', 'Contacto inicial'),
          discover: tr('Discovery Call', 'Llamada inicial'),
          onboard: tr('Client Onboard', 'Incorporación del cliente'),
          research: tr('Market Research', 'Investigación de mercado'),
          offer: tr('Make Offer', 'Presentar oferta'),
          inspection: tr('Home Inspection', 'Inspección de la vivienda'),
          closing: tr('Closing Process', 'Proceso de cierre'),
          complete: tr('Deal Complete', 'Operación completada')
        };

        let currentStage = null;
        let currentNote = null;

        // Find the furthest non-null stage
        for (let i = stages.length - 1; i >= 0; i--) {
          const stage = stages[i];
          if (data[stage] !== null) {
            currentStage = stage;
            currentNote = data[`${stage}Note`];
            break;
          }
        }

        let statusMessage;
        if (currentStage) {
          const stageLabel = stageLabels[currentStage];
          const status = data[currentStage];
          statusMessage = tr(`🏠 **Current Status: ${stageLabel}**\n\n📊 Status: ${status}`, `🏠 **Estado actual: ${stageLabel}**\n\n📊 Estado: ${status}`);
          if (currentNote) {
            statusMessage += tr(`\n📝 Note: ${currentNote}`, `\n📝 Nota: ${currentNote}`);
          }
        } else {
          statusMessage = tr('🏠 **Your Progress**\n\nNo progress recorded yet. Contact us to get started!', '🏠 **Su proceso**\n\nAún no hay avances registrados. ¡Contáctenos para comenzar!');
        }

        const botMessage = { type: 'bot', content: statusMessage };
        setMessages(prev => [...prev, botMessage]);
      } else {
        const botMessage = { type: 'bot', content: tr('No progress information found. Contact us to get started with your real estate journey!', 'No encontramos información sobre su proceso. ¡Contáctenos para comenzar su camino inmobiliario!') };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      console.error('Error fetching CRM data:', error);
      const botMessage = { type: 'bot', content: tr('Unable to retrieve your progress at this time. Please try again later.', 'No podemos consultar su proceso en este momento. Inténtelo de nuevo más tarde.') };
      setMessages(prev => [...prev, botMessage]);
    }
  };

  const fetchLikedProperties = async () => {
    const userEmail = user?.email || email;
    if (!userEmail) {
      const botMessage = { type: 'bot', content: tr('Please sign in to view your liked properties.', 'Inicie sesión para ver sus propiedades favoritas.') };
      setMessages(prev => [...prev, botMessage]);
      return;
    }

    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/interested-buy?email=${encodeURIComponent(userEmail)}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        const likedData = await response.json();
        if (likedData.length === 0) {
          const botMessage = { type: 'bot', content: tr('You haven\'t liked any properties yet.', 'Aún no ha guardado ninguna propiedad.') };
          setMessages(prev => [...prev, botMessage]);
          return;
        }

        const listingKeys = likedData.map(item => item.listingKey);
        const propertiesPromises = listingKeys.map(key =>
          propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?listingKey=${key}`, {}).then(res => res.json())
        );

        const propertiesResults = await Promise.all(propertiesPromises);
        const likedProperties = propertiesResults
          .map(result => result.properties?.[0])
          .filter(prop => prop);

        if (likedProperties.length > 0) {
          const propertiesMessage = {
            type: 'bot',
            content: 'properties',
            properties: likedProperties,
            currentPage: 0
          };
          setMessages(prev => [...prev, propertiesMessage]);
        }
      }
    } catch (error) {
      console.error('Error fetching liked properties:', error);
      const botMessage = { type: 'bot', content: tr('Unable to retrieve your liked properties at this time.', 'No podemos consultar sus propiedades favoritas en este momento.') };
      setMessages(prev => [...prev, botMessage]);
    }
  };

  const handleAction = async (action, params = {}) => {
    // Close any existing forms first
    setShowRegistration(false);
    setShowLogin(false);

    if (action === 'show_registration') {
      setRegEmail(params.email || '');
      setRegName(params.name || '');
      setShowRegistration(true);
    } else if (action === 'show_login') {
      setLoginUsername(params.email || '');
      setShowLogin(true);
    } else if (action === 'show_map') {
      return fetchProperties(params);
    } else if (action === 'collecting_buyer_info') {
      // Clarification does not start a property search.
      return;
    } else if (action === 'get_liked_properties') {
      fetchLikedProperties();
    } else if (action === 'seller_inquiry') {
      // Contact details the visitor shared in conversation arrive on the action params;
      // prefer those over possibly stale local state, then fall back to the capture form.
      const paramName = params.name || '';
      const paramPhone = params.phone || params.phoneNumber || params.phone_number || '';
      const paramEmail = params.email || '';
      if (paramName) setName(paramName);
      if (paramPhone) setPhoneNumber(paramPhone);
      if (paramEmail) setEmail(paramEmail);

      const userEmail = user?.email || paramEmail || email;
      const userName = user?.username || paramName || name;
      const userPhone = user?.phoneNumber || paramPhone || phoneNumber;

      if (userName && userPhone && userEmail) {
        submitSellerInquiry(params, userEmail, userName, userPhone);
        setChatLeadSubmitted(true);
        localStorage.setItem('leadInfo', JSON.stringify({ name: userName, email: userEmail, phone: userPhone }));
      } else {
        setPendingSellerParams(params);
        setMessages(prev => [...prev, { type: 'bot', content: 'seller_lead_capture' }]);
        setTimeout(scrollToBottom, 200);
      }
    } else if (action === 'buying_inquiry') {
      // Per the chat spec, buying_inquiry = serious purchase intent = a LEAD moment, NOT a
      // property browse (browsing/searching returns show_map, which is what renders listings).
      // Contact details shared in conversation arrive on the params (conversational_contact
      // capability, same as the seller flow); use them before falling back to the form.
      const paramName = params.name || '';
      const paramPhone = params.phone || params.phoneNumber || params.phone_number || '';
      const paramEmail = params.email || '';
      if (paramName) setName(paramName);
      if (paramPhone) setPhoneNumber(paramPhone);
      if (paramEmail) setEmail(paramEmail);

      if (user || chatLeadSubmitted) {
        // Known lead: the bot's reply normally drives the next step, but contact info
        // typed into the conversation is deliberate (new or corrected) - record it once.
        if (paramName && paramPhone && paramEmail) {
          const signature = `${paramName}|${paramPhone}|${paramEmail}`;
          if (lastSubmittedLeadRef.current !== signature) {
            lastSubmittedLeadRef.current = signature;
            submitBuyerLead(params, paramName, paramPhone, paramEmail);
          }
        }
      } else {
        const leadName = paramName || name;
        const leadPhone = paramPhone || phoneNumber;
        const leadEmail = paramEmail || email;

        if (leadName && leadPhone && leadEmail) {
          lastSubmittedLeadRef.current = `${leadName}|${leadPhone}|${leadEmail}`;
          submitBuyerLead(params, leadName, leadPhone, leadEmail);
        } else {
          setPendingBuyerParams(params);
          setMessages(prev => [...prev, { type: 'bot', content: 'buyer_lead_capture' }]);
          setTimeout(scrollToBottom, 200);
        }
      }
    } else if (action === 'storeConvo') {
      storeConversation(params);
    } else if (action === 'get-crm') {
      handleGetCRM();
    } else if (action === 'agent_contact_request') {
      const userName = user?.username || name;
      const userPhone = user?.phoneNumber || phoneNumber;

      if (userName && userPhone) {
        sendAgentSMS(userName, userPhone);
      } else {
        setMessages(prev => [...prev, { type: 'bot', content: 'agent_contact_capture' }]);
        setTimeout(scrollToBottom, 200);
      }
    } else if (action === 'property_alert') {
      const searchFilters = searchSnapshot(params.searchFilters || params);

      const subPayload = {
        customerName: params.name,
        agentName: SITE_CONFIG.AGENT.NAME,
        emailAddress: params.email,
        siteId: SITE_CONFIG.SITE_ID,
        cadence: params.cadence || 'daily',
        listingBaseUrl: window.location.origin,
        ...(senderEmail && { senderEmail }),
        ...(senderPassword && { emailPassword: senderPassword }),
        searchFilters,
      };

      const leadParams = new URLSearchParams({
        name: params.name,
        email: params.email,
        postalCode: params.postalCode,
        serviceReq: params.serviceReq || 'buying',
        siteId: SITE_CONFIG.SITE_ID,
      });
      if (params.phoneNumber) leadParams.set('phoneNumber', params.phoneNumber);
      if (params.city) leadParams.set('area', params.city);

      Promise.all([
        fetch('https://tophamtechbe.com/api/treas/comm/property-search-subscriptions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-API-Key': 'treas-api-key-2025' },
          body: JSON.stringify(subPayload),
        }),
        submitChatLead(leadParams, { rememberContact: true }),
      ]).then(([subRes, leadRes]) => {
        if (!subRes.ok || !leadRes.ok) throw new Error('API call failed');
      }).catch(() => {
        setMessages(prev => [...prev, { type: 'bot', content: tr('Sorry, there was an issue setting up your alert. Please try again.', 'Lo sentimos, hubo un problema al crear su alerta. Inténtelo de nuevo.') }]);
        setTimeout(scrollToBottom, 200);
      });
    }
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    try {
      const analyticsIdentity = await getAnalyticsIdentityForAuth();
      const response = await signUp({
        username: regName,
        email: regEmail,
        password: regPassword,
        phoneNumber: regPhoneNumber,
        name: regFullName,
        siteId: SITE_CONFIG.SITE_ID,
        ...(analyticsIdentity ? { analyticsIdentity } : {})
      });

      if (response.ok) {
        // Registration successful, now sign them in automatically
        try {
          const signInResponse = await signIn({
            username: regName,
            password: regPassword,
            siteId: SITE_CONFIG.SITE_ID,
            ...(analyticsIdentity ? { analyticsIdentity } : {})
          });

          if (signInResponse.ok) {
            const userData = await signInResponse.json();
            setUser(userData);
            setIsAdmin(hasAdminAccess(userData));
            localStorage.setItem('user', JSON.stringify(userData));
            completeAnalyticsAuthentication(userData, 'sign_up');

            const botMessage = { type: 'bot', content: tr('Registration successful! You are now signed in. How can I help you today?', '¡Registro completado! Ya inició sesión. ¿Cómo puedo ayudarle hoy?') };
            setMessages(prev => [...prev, botMessage]);
          } else {
            const botMessage = { type: 'bot', content: tr('Registration successful! Please sign in with your new credentials.', '¡Registro completado! Inicie sesión con sus nuevos datos.') };
            setMessages(prev => [...prev, botMessage]);
            setShowLogin(true);
          }
        } catch (error) {
          console.error('Auto sign-in error:', error);
          const botMessage = { type: 'bot', content: tr('Registration successful! Please sign in with your new credentials.', '¡Registro completado! Inicie sesión con sus nuevos datos.') };
          setMessages(prev => [...prev, botMessage]);
          setShowLogin(true);
        }

        setShowRegistration(false);
        setRegEmail('');
        setRegName('');
        setRegPassword('');
        setRegPhoneNumber('');
        setRegFullName('');
        setShowThankYouMessage(true); // Unlock send button
      } else {
        const errorData = await response.text();
        console.log('Registration error response:', errorData);
        const botMessage = { type: 'bot', content: tr('Registration failed. Please try again.', 'No se pudo completar el registro. Inténtelo de nuevo.') };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      console.error('Registration error:', error);
      const botMessage = { type: 'bot', content: tr('Registration failed. Please try again.', 'No se pudo completar el registro. Inténtelo de nuevo.') };
      setMessages(prev => [...prev, botMessage]);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    try {
      const analyticsIdentity = await getAnalyticsIdentityForAuth();
      const response = await signIn({
        username: loginUsername,
        password: loginPassword,
        siteId: SITE_CONFIG.SITE_ID,
        ...(analyticsIdentity ? { analyticsIdentity } : {})
      });

      if (response.ok) {
        const userData = await response.json();

        // Check if siteId matches
        if (userData.siteId?.toLowerCase() !== SITE_CONFIG.SITE_ID.toLowerCase()) {
          const botMessage = { type: 'bot', content: tr('Access denied. You are not authorized for this site.', 'Acceso denegado. No tiene autorización para este sitio.') };
          setMessages(prev => [...prev, botMessage]);
          return;
        }

        // Set user data and check for admin role
        setUser(userData);
        setIsAdmin(hasAdminAccess(userData));
        localStorage.setItem('user', JSON.stringify(userData));
        completeAnalyticsAuthentication(userData, 'login');

        const botMessage = { type: 'bot', content: tr('Login successful! Welcome back. How can I help you today?', '¡Inicio de sesión exitoso! Bienvenido de nuevo. ¿Cómo puedo ayudarle hoy?') };
        setMessages(prev => [...prev, botMessage]);
        setShowLogin(false);
        setLoginUsername('');
        setLoginPassword('');
        setShowThankYouMessage(true); // Unlock send button
      } else {
        const errorData = await response.text();
        console.log('Login error response:', errorData);
        const botMessage = { type: 'bot', content: tr('Login failed. Please check your credentials.', 'No se pudo iniciar sesión. Revise sus datos.') };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      console.error('Login error:', error);
      const botMessage = { type: 'bot', content: tr('Login failed. Please try again.', 'No se pudo iniciar sesión. Inténtelo de nuevo.') };
      setMessages(prev => [...prev, botMessage]);
    }
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await requestPasswordReset({
        email: resetEmail,
        baseUrl: window.location.origin,
        siteId: SITE_CONFIG.SITE_ID
      });

      if (response.ok) {
        const botMessage = { type: 'bot', content: tr('Password reset link sent to your email. Please check your inbox.', 'Enviamos el enlace para restablecer su contraseña. Revise su correo.') };
        setMessages(prev => [...prev, botMessage]);
        setShowForgotPassword(false);
        setShowLogin(false);
        setResetEmail('');
      } else {
        const botMessage = { type: 'bot', content: tr('Failed to send reset link. Please try again.', 'No se pudo enviar el enlace. Inténtelo de nuevo.') };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      console.error('Password reset error:', error);
      const botMessage = { type: 'bot', content: tr('Failed to send reset link. Please try again.', 'No se pudo enviar el enlace. Inténtelo de nuevo.') };
      setMessages(prev => [...prev, botMessage]);
    }
  };

  const fetchProperties = async (params) => {
    chatAbortRef.current?.abort();const controller=new AbortController();chatAbortRef.current=controller;
    const snapshot = chatSearchSnapshot(params, SITE_CONFIG.STATE);
    const generation = chatGenerationRef.current;
    const requestId = ++chatPropertyRequestRef.current;
    const isCurrent = () => generation === chatGenerationRef.current && requestId === chatPropertyRequestRef.current;
    setIsPropertySearching(true);
    try {
      const result = await loadPropertyPage({
        pageSize:itemsPerPage,queryOptions:{firstPhoto:true},signal:controller.signal,
        params: snapshot,
        siteId: SITE_CONFIG.SITE_ID,

        privateApiKey: 'treas-api-key-2025'
      });
      if (!isCurrent()) return;
      // Replace the entire prior search. Merging would resurrect removed filters.
      chatSearchContextRef.current = { siteId: chatSiteId, params: snapshot };
      setMessages(prev => [...prev, {
        type: 'bot',
        content: 'properties',
        properties: result.properties,
        privateUnavailable: result.privateUnavailable,
        pageState:result.pageState,hasMore:result.hasMore,searchFilters:snapshot,searchId:requestId,
        currentPage: 0
      }]);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error fetching chat properties:', error);
      setMessages(prev => [...prev, {
        type: 'bot',
        content: tr("I couldn't load the property search right now.", "No pude cargar la búsqueda de propiedades.") + " " + (error.message || "")
      }]);
    } finally {
      if (isCurrent()) setIsPropertySearching(false);
    }
  };

  const getTileCoords = (lat, lon, zoom) => {
    const x = Math.floor((lon + 180) / 360 * Math.pow(2, zoom));
    const y = Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom));
    return { x, y };
  };

  const getPropertyCoords = (property) => {
    // Use actual lat/lon from property data, fallback to Austin coordinates
    const lat = property.latitude || 30.2672;
    const lon = property.longitude || -97.7431;
    return { lat, lon };
  };

  const isMobileViewport = () => window.innerWidth <= 768;

  const getDefaultSearchLensSize = () => isMobileViewport() ? 'small' : 'medium';

  const getLensDiameter = () => {
    const sizes = isMobileViewport()
      ? { small: 182, medium: 221, large: 273 }
      : { small: 234, medium: 286, large: 364 };
    return sizes[searchLensSize] || sizes.medium;
  };

  const buildPropertySearchParams = (location, includeLocation = true) => {
    const queryParams = new URLSearchParams();

    if (includeLocation && location) {
      if (/^\d{5}$/.test(location.trim())) {
        queryParams.append('zip', location.trim());
      } else if (/\d/.test(location) && /[a-zA-Z]/.test(location)) {
        queryParams.append('address', location.trim());
      } else if (location.length === 2) {
        queryParams.append('state', location.toUpperCase());
      } else {
        const stateMap = {
          'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
          'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
          'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
          'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
          'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
          'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
          'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
          'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
          'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
          'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY'
        };

        const stateAbbr = stateMap[location.toLowerCase().trim()];
        if (stateAbbr) {
          queryParams.append('state', stateAbbr);
        } else {
          queryParams.append('city', location);
        }
      }
    }

    if (propertyType) queryParams.append('propertyType', propertyType);
    if (propertySubType) queryParams.append('propertySubType', propertySubType);
    if (minBedrooms) queryParams.append('minBedrooms', minBedrooms);
    if (maxBedrooms) queryParams.append('maxBedrooms', maxBedrooms);
    if (minPrice) queryParams.append('minPrice', minPrice);
    if (maxPrice) queryParams.append('maxPrice', maxPrice);
    if (minSqft) queryParams.append('minSqft', minSqft);
    if (maxSqft) queryParams.append('maxSqft', maxSqft);
    if (stories) queryParams.append('stories', stories);
    if (minYearBuilt) queryParams.append('minYearBuilt', minYearBuilt);
    if (maxYearBuilt) queryParams.append('maxYearBuilt', maxYearBuilt);
    if (listingKey) queryParams.append('listingKey', listingKey);
    if (hasFence) queryParams.append('hasFence', 'true');
    if (hasPool) queryParams.append('hasPool', 'true');
    if (foundation) queryParams.append('foundation', foundation);
    if (schoolDistrict) queryParams.append('schoolDistrict', schoolDistrict);
    if (communityFeatures.length > 0) {
      communityFeatures.forEach(f => queryParams.append('communityFeatures', f));
    }
    if (acreage === '<5') { queryParams.append('maxLotSizeAcres', '5'); }
    else if (acreage === '5to10') { queryParams.append('minLotSizeAcres', '5'); queryParams.append('maxLotSizeAcres', '10'); }
    else if (acreage === '10+') { queryParams.append('minLotSizeAcres', '10'); }
    if (minBathrooms) queryParams.append('minBathrooms', minBathrooms);
    if (maxBathrooms) queryParams.append('maxBathrooms', maxBathrooms);
    if (priceSort) queryParams.append('priceSort', priceSort);
    if (daysOnMarketSort) queryParams.append('daysonmarketSort', daysOnMarketSort);
    if (listingDateSort) queryParams.append('listingDateSort', listingDateSort);
    if (showAgentListings) queryParams.append('listAgentMlsId', SITE_CONFIG.AGENT.MLS_ID);
    if (hasMedia) queryParams.append('hasMedia', 'true');

    queryParams.set('state', SITE_CONFIG.STATE);
    return queryParams;
  };

  const getCurrentPropertySearchFilters = (location) => ({
    location,
    propertyType,
    propertySubType,
    minBedrooms,
    maxBedrooms,
    minPrice,
    maxPrice,
    minSqft,
    maxSqft,
    stories,
    minYearBuilt,
    maxYearBuilt,
    listingKey,
    hasFence,
    hasPool,
    foundation,
    schoolDistrict,
    communityFeatures: [...communityFeatures],
    acreage,
    minBathrooms,
    maxBathrooms,
    priceSort,
    listingDateSort,
    showAgentListings,
    hasMedia
  });

  const getDefaultSearchLensCenter = () => ({
    lat: parseFloat(SEO_CONFIG.LATITUDE) || 30.2672,
    lon: parseFloat(SEO_CONFIG.LONGITUDE) || -97.7431
  });

  const getDefaultSearchLensLabel = () => {
    const parts = [SEO_CONFIG.CITY, SEO_CONFIG.STATE].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Search Lens';
  };

  const geocodeSearchLocation = async (location) => {
    if (!location?.trim()) return getDefaultSearchLensCenter();

    try {
      const query = `${location}, ${SITE_CONFIG.STATE}`;
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`);
      const data = await response.json();
      if (data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lon: parseFloat(data[0].lon)
        };
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    return getDefaultSearchLensCenter();
  };

  const getLensQuery = (map) => {
    const position = searchLensPositionRef.current;
    if (!map || !position) return null;
    const radiusPixels = getLensDiameter() / 2;
    const centerPoint = L.point(position.x, position.y);
    const edgePoint = L.point(position.x + radiusPixels, position.y);
    const centerLatLng = map.containerPointToLatLng(centerPoint);
    const edgeLatLng = map.containerPointToLatLng(edgePoint);
    const radiusMiles = centerLatLng.distanceTo(edgeLatLng) / 1609.344;
    return { centerLatLng, radiusMiles };
  };

  const renderPropertyMarkers = (map, propertyList, useClusters) => {
    if (!map) return;
    if (globeMarkersRef.current) {
      map.removeLayer(globeMarkersRef.current);
      globeMarkersRef.current = null;
    }

    if (!propertyList || propertyList.length === 0) {
      const locationIcon = L.divIcon({
        html: '📍',
        className: 'location-marker',
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });
      const marker = L.marker([map.getCenter().lat, map.getCenter().lng], { icon: locationIcon });
      globeMarkersRef.current = marker.addTo(map);
      return;
    }

    const layer = useClusters
      ? L.markerClusterGroup({
          maxClusterRadius: 50,
          spiderfyOnMaxZoom: true,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true
        , iconCreateFunction: cluster => clusterBubbleIcon(cluster.getChildCount())})
      : L.layerGroup();

    propertyList.forEach(property => {
      if (property.latitude && property.longitude) {
        const price = property.current_price || property.price || 'N/A';
        const houseIcon = propertyMapIcon(property.current_price ?? property.price,{isPrivate:property.isPrivate});

        const marker = L.marker([property.latitude, property.longitude], { icon: houseIcon });
        const listingKey = property.listing_key;

        marker.on('mouseover', function () {
          prefetchPropertyDetail(property);
          const cachedImg = propertyImagesRef.current[listingKey];
          if (cachedImg) {
            if (!useClusters) {
              const point = map.latLngToContainerPoint([property.latitude, property.longitude]);
              setSearchLensPreview({
                imageUrl: cachedImg,
                x: point.x,
                y: point.y
              });
              return;
            }
            this.bindTooltip(
              `<img src="${cachedImg}" class="thumbnail-tooltip-image" />`,
              {
                direction: 'top',
                offset: [0, -10],
                className: 'thumbnail-tooltip',
                pane: map.getPane('searchLensPreviewPane') ? 'searchLensPreviewPane' : 'tooltipPane'
              }
            ).openTooltip();
          }
        });
        marker.on('mouseout', function () {
          if (!useClusters) {
            setSearchLensPreview(null);
            return;
          }
          this.unbindTooltip();
        });
        marker.on('click', () => {
          setSearchLensPreview(null);
          if (property.isPrivate) {
            fetchPrivatePropertyDetail(property);
          } else if (property.id) {
            fetchPropertyDetail(property.id, property);
          }
        });

        layer.addLayer(marker);
      }
    });

    layer.addTo(map);
    globeMarkersRef.current = layer;
  };

  const fetchSearchLensProperties = async (map) => {
    const activeSearchParams = searchLensParamsRef.current || globeMap?.searchParams;
    if (!globeMap?.isSearchLens || !activeSearchParams) return;
    const lensQuery = getLensQuery(map);
    if (!lensQuery) return;

    const requestId = searchLensRequestRef.current + 1;
    searchLensRequestRef.current = requestId;
    setSearchLensLoading(true);
    setSearchLensRadiusMiles(lensQuery.radiusMiles);

    const queryParams = searchQuery(activeSearchParams);
    queryParams.set('centerLat', lensQuery.centerLatLng.lat.toFixed(6));
    queryParams.set('centerLng', lensQuery.centerLatLng.lng.toFixed(6));
    queryParams.set('radiusMiles', lensQuery.radiusMiles.toFixed(4));

    try {
      assertValidSearch(searchFromQuery(queryParams));
      setManualSearchError('');
      lensAbortRef.current?.abort();const controller=new AbortController();lensAbortRef.current=controller;
      const result=await loadPropertyMap({params:searchFromQuery(queryParams),bounds:viewportBounds(map),zoom:map.getZoom(),signal:controller.signal});
      const data={properties:[],total_count:result.map.match_count};

      if (searchLensRequestRef.current !== requestId) return;
      const nextProperties = data.properties || [];
      appliedSearchRef.current = searchFromQuery(queryParams);
      setSearchLensProperties(nextProperties);setLensMapData(result.map);
      setSearchLensResultCount(data.total_count ?? nextProperties.length);
      const analyticsSignature = `${queryParams.toString()}|${data.total_count ?? nextProperties.length}`;
      if (lastTrackedSearchLensRef.current !== analyticsSignature) {
        lastTrackedSearchLensRef.current = analyticsSignature;
        trackPropertySearch({
          searchTerm: globeMap.location,
          filters: activeSearchParams,
          results: nextProperties,
          resultCount: data.total_count ?? nextProperties.length,
          surface: 'search_lens'
        });
      }
      nextProperties.slice(0, 25).forEach(property => {
        if (property.listing_key && !propertyImagesRef.current[property.listing_key]) {
          fetchPropertyImage(property.listing_key);
        }
      });
    } catch (error) {
      if(error.name==='AbortError')return;
      if (searchLensRequestRef.current === requestId) {
        console.error('Error fetching Search Lens properties:', error);
        setManualSearchError(error.message || 'Unable to load Search Lens results.');
      }
    } finally {
      if (searchLensRequestRef.current === requestId) {
        setSearchLensLoading(false);
      }
    }
  };

  const scheduleSearchLensFetch = (map, delay = 350) => {
    lensAbortRef.current?.abort();searchLensRequestRef.current++;
    if (!map) return;
    clearTimeout(searchLensDebounceRef.current);
    searchLensDebounceRef.current = setTimeout(() => fetchSearchLensProperties(map), delay);
  };

  const handleSearchLensSizeChange = (size) => {
    setSearchLensSize(size);
    setTimeout(() => scheduleSearchLensFetch(globeLeafletRef.current, 0), 0);
  };

  const updateSearchLensParam = (key, value) => {
    const current = searchLensParamsRef.current || globeMap?.searchParams || {};
    const next = { ...current };
    if (value) {
      next[key] = value;
    } else {
      delete next[key];
    }
    next.state = SITE_CONFIG.STATE;
    searchLensParamsRef.current = next;
    scheduleSearchLensFetch(globeLeafletRef.current, 0);
  };

  const handleSearchLensMaxPriceChange = (value) => {
    setMaxPrice(value === '0' ? '' : value);
    updateSearchLensParam('maxPrice', value === '0' ? '' : value);
  };

  const handleSearchLensBedroomsChange = (value) => {
    setMinBedrooms(value === '0' ? '' : value);
    updateSearchLensParam('minBedrooms', value === '0' ? '' : value);
  };

  const getPointerPosition = (event) => {
    const source = event.touches?.[0] || event.changedTouches?.[0] || event;
    return { clientX: source.clientX, clientY: source.clientY };
  };

  const clampLensPosition = (x, y) => {
    const container = globeMapRef.current;
    if (!container) return { x, y };
    const rect = container.getBoundingClientRect();
    const radius = getLensDiameter() / 2;
    return {
      x: Math.min(Math.max(x, radius), rect.width - radius),
      y: Math.min(Math.max(y, radius), rect.height - radius)
    };
  };

  const handleSearchLensDragStart = (event) => {
    if (!globeMap?.isSearchLens || !searchLensPosition) return;
    event.preventDefault();
    event.stopPropagation();
    const pointer = getPointerPosition(event);
    searchLensDragRef.current = {
      startX: pointer.clientX,
      startY: pointer.clientY,
      originX: searchLensPosition.x,
      originY: searchLensPosition.y
    };
    setSearchLensDragging(true);
  };

  useEffect(() => {
    if (!searchLensDragging) return;

    const handleMove = (event) => {
      if (!searchLensDragRef.current) return;
      event.preventDefault();
      const pointer = getPointerPosition(event);
      const next = clampLensPosition(
        searchLensDragRef.current.originX + pointer.clientX - searchLensDragRef.current.startX,
        searchLensDragRef.current.originY + pointer.clientY - searchLensDragRef.current.startY
      );
      setSearchLensPosition(next);
      searchLensPositionRef.current = next;
    };

    const handleEnd = () => {
      setSearchLensDragging(false);
      searchLensDragRef.current = null;
      scheduleSearchLensFetch(globeLeafletRef.current);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
    window.addEventListener('touchcancel', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
      window.removeEventListener('touchcancel', handleEnd);
    };
  }, [searchLensDragging]); // eslint-disable-line react-hooks/exhaustive-deps -- listeners are rebound only when drag state changes

  const persistFavoriteKeys = (favoriteKeys) => {
    try {
      writeSaveStorage(localFavoritesKey, [...favoriteKeys]);
    } catch (error) {
      // CRM saving remains authoritative if browser storage is unavailable.
    }
  };

const additionalFilterCount = [
    propertySubType, maxBedrooms, minSqft, maxSqft, stories, foundation,
    minYearBuilt, maxYearBuilt, schoolDistrict, listingKey, acreage,
    minBathrooms, maxBathrooms, hasFence, hasPool, showAgentListings,
    searchLensEnabled, priceSort, listingDateSort, ...communityFeatures
  ].filter(Boolean).length + Object.entries(advancedFilters).filter(([key, value]) =>
    key !== 'state' && value !== null && value !== undefined && value !== '' &&
    (!Array.isArray(value) || value.length > 0)
  ).length;
  useEffect(() => {
    if (!globeMap?.isSavedHomes) return;
    const list=globeMap.savedListId ? collections.find(row=>row.id===globeMap.savedListId) : null;
    const activeKeys=new Set((globeMap.savedListId ? list?.listingKeys || [] : [...likedProperties]).map(String));
    const properties=globeMap.properties.filter(property=>activeKeys.has(String(property.listing_key)));
    if(properties.length===globeMap.properties.length)return;
    if(!properties.length){setPropertyDetail(null);setGlobeMap(null);setMapView(true);window.history.replaceState(null,'','/search');return;}
    setGlobeMap(current=>({...current,properties}));
  },[likedProperties,collections]); // eslint-disable-line react-hooks/exhaustive-deps -- saved membership changes prune this view
  const handleViewSavedHomes = async (listKeys, listName, listId, editingListId) => {
    if (savedHomesLoading || (listKeys ? !listKeys.length : likedProperties.size === 0)) return;

    setSavedHomesLoading(true);
    setSavedHomesError('');
    const savedKeys = [...(listKeys || likedProperties)].filter(Boolean);
    let lookupFailed = false;

    try {
      const publicKeys = savedKeys.filter((key) => !String(key).startsWith('PRIVATE_'));
      const privateKeys = new Set(savedKeys.filter((key) => String(key).startsWith('PRIVATE_')).map(String));

      const publicProperties = await Promise.all(publicKeys.map(async (key) => {
        try {
          const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?listingKey=${encodeURIComponent(key)}`);
          if (!response.ok) {
            if (response.status >= 500 || response.status === 429) lookupFailed = true;
            return null;
          }
          const data = await response.json();
          const property = data.properties?.[0];
          return property ? { ...property, listing_key: property.listing_key || key } : null;
        } catch (error) {
          lookupFailed = true;
          return null;
        }
      }));

      let privateProperties = [];
      if (privateKeys.size > 0) {
        try {
          const response = await fetch(`https://tophamtechbe.com/api/treas/private/v1/listings?site_id=${SITE_CONFIG.SITE_ID}`, {
            headers: { 'x-api-key': 'treas-api-key-2025' }
          });
          if (!response.ok) {
            lookupFailed = true;
          } else {
            const data = await response.json();
            privateProperties = (data.listings || [])
              .map((listing) => ({
                ...listing,
                listing_key: `PRIVATE_${listing.id}`,
                current_price: listing.listing_type === 'sale' ? listing.price_sale : listing.price_lease_monthly,
                bedrooms_total: listing.beds,
                address: `${listing.address_line_1}, ${listing.city}, ${listing.state_province}`,
                isPrivate: true
              }))
              .filter((property) => privateKeys.has(String(property.listing_key)));
          }
        } catch (error) {
          lookupFailed = true;
        }
      }

      const savedProperties = [...publicProperties.filter(Boolean), ...privateProperties];
      if (savedProperties.length === 0) {
        if (lookupFailed || listKeys) {
          setSavedHomesError('Saved homes could not be loaded right now. Please try again.');
        } else {
          const emptyFavorites = new Set();
          setLikedProperties(emptyFavorites);
          persistFavoriteKeys(emptyFavorites);
        }
        return;
      }

      if (!lookupFailed && !listKeys) {
        const activeKeys = new Set(savedProperties.map((property) => property.listing_key));
        setLikedProperties(activeKeys);
        persistFavoriteKeys(activeKeys);
      }

      const mappableProperties = savedProperties.filter((property) => property.latitude && property.longitude);
      const center = mappableProperties.length > 0
        ? {
            lat: mappableProperties.reduce((sum, property) => sum + Number(property.latitude), 0) / mappableProperties.length,
            lon: mappableProperties.reduce((sum, property) => sum + Number(property.longitude), 0) / mappableProperties.length
          }
        : {
            lat: Number(SEO_CONFIG.LATITUDE) || 30.2672,
            lon: Number(SEO_CONFIG.LONGITUDE) || -97.7431
          };

      setListViewPage(0);
      setMapView(false);
      setShowAlertPill(false);
      alertTriggeredRef.current = true;
      setGlobeMap({
        center,
        location: listName || 'Saved homes',
        savedListId: listId || null,
        editingListId: editingListId || null,
        properties: savedProperties,
        searchFilters: {},
        isSavedHomes: true
      });
      window.history.replaceState(null, '', '/search?saved=true');
    } catch (error) {
      setSavedHomesError('Saved homes could not be loaded right now. Please try again.');
    } finally {
      setSavedHomesLoading(false);
    }
  };

  const handleGlobeMapSubmit = async e=>{e.preventDefault();if(isGlobeMapLoading||(!locationInput.trim()&&!listingKey&&!searchLensEnabled))return;setIsGlobeMapLoading(true);try{await fetchPropertiesForGlobe(locationInput.trim(),searchLensEnabled);}finally{setIsGlobeMapLoading(false);}};

  const loadMapPreview = (property) => {
    const key=property.listing_key;
    const cached=propertyImagesRef.current[key] || property.first_photo_url;
    if(cached || !key)return Promise.resolve(cached || null);
    if(!mapPreviewRequestsRef.current.has(key)) {
      const request=fetchPropertyImage(key).finally(()=>mapPreviewRequestsRef.current.delete(key));
      mapPreviewRequestsRef.current.set(key,request);
    }
    return mapPreviewRequestsRef.current.get(key);
  };

  const fetchPropertiesForGlobe = async (location, useSearchLens = false) => {
    const request=++initialMapRequestRef.current;initialMapAbortRef.current?.abort();const controller=new AbortController();initialMapAbortRef.current=controller;setMapStats({});setLensMapData(null);
    try {
      const queryParams = buildPropertySearchParams(location, !useSearchLens);
      applyManualFilters(queryParams);

      if (useSearchLens) {
        const displayLocation = location || getDefaultSearchLensLabel();
        const center = await geocodeSearchLocation(location);
        if(request!==initialMapRequestRef.current)return;
        const searchParams = searchFromQuery(queryParams);
        searchLensParamsRef.current = searchParams;
        setSearchLensSize(getDefaultSearchLensSize());
        setSearchLensProperties([]);
        setSearchLensResultCount(0);
        setSearchLensRadiusMiles(null);
        setMapView(true);
        setGlobeMap({ center, location: displayLocation, properties: [], isSearchLens: true, searchParams });

        const shareParams = searchQuery(searchParams);
        if (location) shareParams.set('location', location);
        shareParams.delete('q');
        shareParams.set('searchLens', 'true');
        window.history.replaceState(null, '', `/search?${shareParams.toString()}`);
        return;
      }

      const filters=searchFromQuery(queryParams);appliedSearchRef.current=filters;
      const shareParams=searchQuery(filters);if(location)shareParams.set('location',location);

      const [result,privateResult]=await Promise.all([
        loadPropertyMap({params:filters,zoom:4,signal:controller.signal}),
        loadPrivateMapRows({params:filters,siteId:SITE_CONFIG.SITE_ID,privateApiKey:'treas-api-key-2025',signal:controller.signal})]);
      if(request!==initialMapRequestRef.current)return;
      trackPropertySearch({searchTerm:location,filters,results:result.map.features.filter(feature=>feature.kind==='property'),resultCount:result.map.match_count,surface:'desktop_map'});
      const point=result.map.features[0];
      const center=point?{lat:Number(point.latitude),lon:Number(point.longitude)}:{lat:Number(SEO_CONFIG.LATITUDE)||30,lon:Number(SEO_CONFIG.LONGITUDE)||-97};
      setMapView(true);setGlobeMap({center,location,properties:[],mapData:result.map,privateRows:privateResult.properties,privateUnavailable:privateResult.privateUnavailable,searchParams:filters});
      setMapStats({count:result.map.match_count,loading:false,error:''});setMapView(true);setListViewPage(0);
      if(new URLSearchParams(window.location.search).get('view') === 'map')shareParams.set('view','map');
      window.history.replaceState(window.history.state,'',`/search?${shareParams}`);
    } catch (error) {
      if(error.name==='AbortError'||request!==initialMapRequestRef.current)return;
      console.error('Error fetching properties for globe:', error);
      setManualSearchError(error.message || 'Unable to load property search.');
    }
  };

  useEffect(() => {
    let detachBoundedMap;
    let resizeObserver;
    let resizeFrame;
    if (globeMap && globeMapRef.current && mapView) {
      if (globeLeafletRef.current) {
        globeLeafletRef.current.remove();
      }

      const map = L.map(globeMapRef.current,{minZoom:1,maxZoom:20}).setView([globeMap.center.lat, globeMap.center.lon], globeMap.isSearchLens ? 14 : 12);

      // The results panel replaces the filter form in the same render. Leaflet can
      // measure the new container before the browser has laid it out, leaving the
      // first map with an empty viewport until another search remounts it.
      const syncMapSize = () => map.invalidateSize({ pan: false });
      resizeFrame = window.requestAnimationFrame(syncMapSize);
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(syncMapSize);
        resizeObserver.observe(globeMapRef.current);
      }

      addIllustratedBasemap(map);
      map.zoomControl.setPosition('bottomleft');
      if(globeMap.mapData){
        const pane=map.createPane('searchLensPreviewPane');pane.classList.add('search-lens-preview-pane');pane.style.zIndex='10050';pane.style.pointerEvents='none';
        detachBoundedMap=attachBoundedMap(map,{params:globeMap.searchParams,initialMap:globeMap.mapData,privateRows:globeMap.privateRows,getPreview:loadMapPreview,onProperty:p=>p.isPrivate?fetchPrivatePropertyDetail(p):fetchPropertyDetail(p.id,p),onStatus:value=>setMapStats(prev=>({...prev,...value}))});
      }else {
      const previewPane = map.createPane('searchLensPreviewPane');
      previewPane.classList.add('search-lens-preview-pane');
      previewPane.style.zIndex = '10050';
      previewPane.style.pointerEvents = 'none';
      if (globeMap.isSearchLens) {
        const size = map.getSize();
        const initialPosition = clampLensPosition(size.x / 2, isMobileViewport() ? size.y * 0.42 : size.y / 2);
        setSearchLensPosition(initialPosition);
        searchLensPositionRef.current = initialPosition;

        const updateLensResults = () => scheduleSearchLensFetch(map);
        const clearPreview = () => setSearchLensPreview(null);
        map.on('moveend', updateLensResults);
        map.on('zoomend', updateLensResults);
        map.on('movestart', clearPreview);
        map.on('zoomstart', clearPreview);
        setTimeout(() => scheduleSearchLensFetch(map, 0), 0);
      } else {
        renderPropertyMarkers(map, globeMap.properties || [], true);

        map.on('moveend', () => {
          if (map.getZoom() < 14) return;
          const bounds = map.getBounds();
          let fetched = 0;
          globeMap.properties.forEach(p => {
            if (fetched >= 25) return;
            if (p.latitude && p.longitude && p.listing_key && !propertyImagesRef.current[p.listing_key]) {
              if (bounds.contains([p.latitude, p.longitude])) {
                fetchPropertyImage(p.listing_key);
                fetched++;
              }
            }
          });
        });

        map.fire('moveend');
      }

      }
      globeLeafletRef.current = map;
      setHybridMapInstance(map);
    }

    return () => {
      if (resizeFrame !== undefined) window.cancelAnimationFrame(resizeFrame);
      resizeObserver?.disconnect();
      detachBoundedMap?.();lensAbortRef.current?.abort();
      clearTimeout(searchLensDebounceRef.current);
      if (globeLeafletRef.current) {
        globeLeafletRef.current.remove();
        globeLeafletRef.current = null;
        setHybridMapInstance(null);
        globeMarkersRef.current = null;
      }
    };
  }, [globeMap, mapView]); // eslint-disable-line react-hooks/exhaustive-deps -- map lifecycle is owned by these two state values

  useEffect(()=>{if(!globeMap?.isSearchLens||!globeLeafletRef.current||!mapView)return;if(globeMarkersRef.current)globeLeafletRef.current.removeLayer(globeMarkersRef.current);globeMarkersRef.current=renderBoundedFeatures(globeLeafletRef.current,lensMapData?.features||[],{params:appliedSearchRef.current,getPreview:loadMapPreview,onProperty:p=>fetchPropertyDetail(p.id,p)});},[lensMapData,globeMap,mapView]); // eslint-disable-line react-hooks/exhaustive-deps -- marker rendering follows map data, not function identity

  // Warm the detail cache for the map pills currently in view once the user has zoomed in
  // enough to see a small set (<= 7), so clicking any pill opens its modal snappily.
  useEffect(() => {
    if (!mapView || !globeMap) return;
    const visible = globeMap.isSearchLens ? searchLensProperties : globeMap.properties;
    if (Array.isArray(visible) && visible.length > 0 && visible.length <= 7) {
      visible.forEach((p) => prefetchPropertyDetail(p));
    }
  }, [searchLensProperties, globeMap, mapView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Alert pill trigger: show after 4s or on first map interaction
  useEffect(() => {
    if (!globeMap || globeMap.isSearchLens || !globeLeafletRef.current || alertTriggeredRef.current) return;
    const trigger = () => {
      if (alertTriggeredRef.current) return;
      alertTriggeredRef.current = true;
      setShowAlertPill(true);
    };
    const timer = setTimeout(trigger, 4000);
    const map = globeLeafletRef.current;
    map.on('zoomend', trigger);
    return () => {
      clearTimeout(timer);
      if (map) map.off('zoomend', trigger);
    };
  }, [globeMap, mapView]);

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setServiceStep(0);
    setShowThankYouMessage(false);
    setTimeout(scrollToBottom, 200);
  };

  const handleServiceStepContinue = (event) => {
    event.preventDefault();
    if (serviceStep < 3) {
      setServiceStep((step) => step + 1);
      setTimeout(scrollToBottom, 160);
      return;
    }
    handleFormSubmit(event);
  };

  const handleNewConversation = () => {
    chatAbortRef.current?.abort();moreRequestsRef.current.forEach(controller=>controller.abort());moreRequestsRef.current.clear();
    chatGenerationRef.current += 1;
    chatPropertyRequestRef.current += 1;
    chatSearchContextRef.current = null;
    chatRequestInFlightRef.current = false;
    setMessages([]);
    setSelectedService(null);
    setServiceStep(0);
    setShowThankYouMessage(false);
    setShowRegistration(false);
    setShowLogin(false);
    setShowForgotPassword(false);
    setPendingBuyerParams(null);
    setPendingSellerParams(null);
    setInputValue('');
    setIsThinking(false);
    setIsPropertySearching(false);
    scrollMobileWorkspaceTo('chat', 'auto');
  };

  const handleShowMoreProperties = async (messageIndex) => {
    const msg=messages[messageIndex];if(!msg||msg.loadingMore)return;
    if(msg.restartSearch){await fetchProperties(msg.searchFilters);return;}
    const nextPage=(msg.currentPage||0)+1;
    if(!msg.pageState||nextPage*itemsPerPage<msg.properties.length) {
      setMessages(prev=>prev.map((item,index)=>index===messageIndex?{...item,currentPage:nextPage}:item));return;
    }
    if(!msg.hasMore||moreRequestsRef.current.has(msg.searchId))return;
    const generation=chatGenerationRef.current;const controller=new AbortController();moreRequestsRef.current.set(msg.searchId,controller);
    const update=change=>setMessages(prev=>prev.map(item=>item.searchId===msg.searchId&&item.content==='properties'?{...item,...change}:item));
    update({loadingMore:true,pageError:''});
    try {
      const result=await loadPropertyPage({params:msg.searchFilters,pageState:msg.pageState,signal:controller.signal,pageSize:itemsPerPage,siteId:SITE_CONFIG.SITE_ID,queryOptions:{firstPhoto:true},privateApiKey:'treas-api-key-2025'});
      if(generation!==chatGenerationRef.current)return;
      const seen=new Set(msg.properties.map(p=>p.listing_key));
      update({properties:[...msg.properties,...result.properties.filter(p=>!seen.has(p.listing_key))],pageState:result.pageState,hasMore:result.hasMore,currentPage:nextPage,loadingMore:false});
    }catch(error){if(generation===chatGenerationRef.current&&error.name!=='AbortError')update({loadingMore:false,pageError:error.message,restartSearch:error.restartSearch});}
    finally{moreRequestsRef.current.delete(msg.searchId);}
  };

  const markCaptureFormSubmitted = (content) => {
    setMessages(prev => prev.map(msg => msg.content === content ? { ...msg, submitted: true } : msg));
  };

  const handleSellerLeadCaptureSubmit = async (e) => {
    e.preventDefault();
    if (!isHoneypotSubmission(e.currentTarget)) await submitSellerInquiry(pendingSellerParams, email, name, phoneNumber);
    setPendingSellerParams(null);
    setChatLeadSubmitted(true);
    localStorage.setItem('leadInfo', JSON.stringify({ name, email, phone: phoneNumber }));
    markCaptureFormSubmitted('seller_lead_capture');
    const botMessage = { type: 'bot', content: tr(`Thanks ${name}! We've noted your property details and will be in touch shortly.`, `¡Gracias, ${name}! Guardamos los detalles de su propiedad y pronto nos comunicaremos con usted.`) };
    setMessages(prev => [...prev, botMessage]);
    setTimeout(scrollToBottom, 200);
  };

  const submitBuyerLead = async (buyerParams, leadName, leadPhone, leadEmail, suppressLead = false) => {
    const p = buyerParams || {};
    const area = p.city || zipCode;
    const postalCode = p.zip || (zipCode.match(/^\d{5}$/) ? zipCode : '');

    const leadParams = new URLSearchParams({
      name: leadName,
      email: leadEmail,
      phoneNumber: leadPhone,
      serviceReq: 'buying',
      siteId: SITE_CONFIG.SITE_ID,
    });
    if (area) leadParams.set('area', area);
    if (postalCode) leadParams.set('postalCode', postalCode);

    try {
      if (!suppressLead) await submitChatLead(leadParams, { rememberContact: true });
    } catch (error) {
      console.error('Error submitting buyer lead:', error);
    }

    localStorage.setItem('leadInfo', JSON.stringify({ name: leadName, email: leadEmail, phone: leadPhone }));
    setChatLeadSubmitted(true);

    const searchParams = {...p};
    if (!searchParams.city && !searchParams.zip && zipCode) searchParams.zip = zipCode;
    if (!searchParams.maxPrice && maxPrice) searchParams.maxPrice = maxPrice;
    fetchProperties(searchParams);
    setMessages(prev => [...prev, { type: 'bot', content: tr(`Thanks ${leadName}! Here are properties matching your search.`, `¡Gracias, ${leadName}! Estas propiedades coinciden con su búsqueda.`) }]);
    setTimeout(scrollToBottom, 200);
  };

  const handleBuyerLeadCaptureSubmit = async (e) => {
    e.preventDefault();
    markCaptureFormSubmitted('buyer_lead_capture');
    const buyerParams = pendingBuyerParams;
    setPendingBuyerParams(null);
    await submitBuyerLead(buyerParams, name, phoneNumber, email, isHoneypotSubmission(e.currentTarget));
  };

  const handleSellingFormSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = isHoneypotSubmission(e.currentTarget) ? { ok: true } : await fetch(`https://tophamtechbe.com/api/treas/idx/v1/selling-details?name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}&phoneNumber=${encodeURIComponent(phoneNumber)}&address=${encodeURIComponent(propertyLocation)}&size=${encodeURIComponent(propertySize)}&condition=${encodeURIComponent(propertyCondition)}&timeline=${encodeURIComponent(sellingTimeline)}&siteId=${SITE_CONFIG.SITE_ID}`, {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        console.log('Selling details submitted successfully');
      } else {
        console.error('Failed to submit selling details');
      }
    } catch (error) {
      console.error('Error submitting selling details:', error);
    }

    // Show completion message
    const botMessage = { type: 'bot', content: tr(`Thank you! I have all the details about your property. Giovanni Triana will contact you soon to discuss selling your ${propertySize} property in ${propertyLocation}.`, `¡Gracias! Ya tengo los detalles de su propiedad. Giovanni Triana se comunicará pronto para hablar sobre la venta de su propiedad de ${propertySize} en ${propertyLocation}.`) };
    setMessages(prev => [...prev, botMessage]);
    setTimeout(scrollToBottom, 200);

    // Clear selling form but keep chat enabled
    setPropertyLocation('');
    setPropertySize('');
    setPropertyCondition('');
    setSellingTimeline('');
    // Don't set showThankYouMessage to false - keep send button enabled
  };

  const fetchPrivatePropertyDetail = async (listing) => {
    setPropertyDetailImages([]);
    setImagesLoading(true);

    const listingId = String(listing.listing_key).replace('PRIVATE_', '');

    // If listing data is incomplete (e.g. coming from a direct URL), fetch it first
    let fullListing = listing;
    if (!listing.current_price && !listing.address) {
      try {
        const detailResponse = await fetch(`https://tophamtechbe.com/api/treas/private/v1/listing/${listingId}?site_id=${SITE_CONFIG.SITE_ID}`, {
          headers: { 'x-api-key': 'treas-api-key-2025' }
        });
        if (detailResponse.ok) {
          const detailData = await detailResponse.json();
          const d = detailData.listing || detailData;
          fullListing = {
            ...d,
            listing_key: `PRIVATE_${d.id}`,
            current_price: d.listing_type === 'sale' ? d.price_sale : d.price_lease_monthly,
            bedrooms_total: d.beds,
            address: `${d.address_line_1}, ${d.city}, ${d.state_province}`,
            isPrivate: true
          };
        }
      } catch (error) {
        console.error('Error fetching private listing detail:', error);
      }
    }

    try {
      const mediaResponse = await fetch(`https://tophamtechbe.com/api/treas/media/private/v1/media/${SITE_CONFIG.SITE_ID}/${listingId}`, {
        headers: { 'x-api-key': 'treas-api-key-2025' }
      });

      if (mediaResponse.ok) {
        const mediaData = await mediaResponse.json();
        const sorted = (mediaData.media || []).sort((a, b) => a.order_hint - b.order_hint);
        setPropertyDetailImages(sorted.map(img => img.media_url || img.s3_link));
      }
    } catch (error) {
      console.error('Error fetching private property media:', error);
    } finally {
      setImagesLoading(false);
    }

    const address = fullListing.address || '';
    setPropertyDetail({
      ...fullListing,
      listing_key: fullListing.listing_key,
      current_price: fullListing.current_price,
      bedrooms_total: fullListing.bedrooms_total,
      virtual_tour_url: fullListing.virtual_tour_url,
      created_at: fullListing.created_at,
      updated_at: fullListing.updated_at,
      location: {
        street_number: address.split(',')[0] || '',
        city: fullListing.city || '',
        state: fullListing.state_province || '',
        postal_code: fullListing.postal_code || ''
      },
      features: {
        public_remarks: fullListing.description || ''
      },
      isPrivate: true
    });
  };

  const warmedImageUrls = useRef(new Set());
  // Preload an existing summary photo without requesting unopened property details.
  const prefetchPropertyDetail = (property) => {
    if (navigator.connection?.saveData) return;
    warmImage(property?.first_photo_url);
  };

  const warmImage = (url) => {
    if (!url || warmedImageUrls.current.has(url)) return;
    warmedImageUrls.current.add(url);
    const im = new Image();
    im.src = url;
  };

  // Preload the current and next page summary photos.
  React.useEffect(() => {
    const conn = navigator.connection || {};
    if (conn.saveData) return;
    messages.forEach((msg) => {
      if (msg?.content !== 'properties' || !Array.isArray(msg.properties)) return;
      const page = msg.currentPage || 0;
      const visibleEnd = (page + 1) * itemsPerPage;
      const nextEnd = (page + 2) * itemsPerPage;
      msg.properties.slice(0, visibleEnd).forEach((p) => prefetchPropertyDetail(p));
      msg.properties.slice(visibleEnd, nextEnd).forEach((p) => {
        prefetchPropertyDetail(p);
        warmImage(p?.first_photo_url);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, itemsPerPage]);

  const fetchPropertyDetail = async (propertyId, summaryData) => {
    const requestId = ++detailRequestRef.current;
    const cover = summaryData?.first_photo_url || summaryData?.image_url || summaryData?.photo_url;
    // Optimistic open: paint the modal INSTANTLY from the summary the clicked tile/pill
    // already has (price, address, and the first photo — already cached as the thumbnail),
    // so it doesn't wait on the (sometimes cold) detail round-trip. Enrich below.
    if (summaryData) {
      setPropertyDetail({
        ...summaryData,
        location: {
          street_number: summaryData.street_number,
          street_dir_prefix: summaryData.street_dir_prefix,
          street_suffix: summaryData.street_suffix,
          street_dir_suffix: summaryData.street_dir_suffix,
          unit_number: summaryData.unit_number,

          street_name: summaryData.street_name,
          city: summaryData.city,
          state: summaryData.state,
          postal_code: summaryData.postal_code
        },
        features: summaryData.features || {}
      });
      setPropertyDetailImages(cover ? [cover] : []);
      setImagesLoading(!cover);
    } else {
      setPropertyDetailImages([]);
      setImagesLoading(true);
    }

    try {
      const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-data/${propertyId}`, {});
      if (requestId !== detailRequestRef.current) return;

      if (response.ok) {
        const data = await response.json();
        const property = data.properties[0];
        setPropertyDetail({
          ...property,
          created_at: property.created_at || summaryData?.created_at,
          updated_at: property.updated_at || summaryData?.updated_at
        });

        // Fetch all images for the property detail view
        if (property.listing_key) {
          fetchAllPropertyImages(property.listing_key, requestId, cover);
        }
      } else {
        console.error('Failed to fetch property details');
        setImagesLoading(false);
      }
    } catch (error) {
      console.error('Error fetching property details:', error);
      if (requestId === detailRequestRef.current) setImagesLoading(false);
    }
  };

  const fetchPropertyDetailByListingKey = async (listingKey) => {
    if (String(listingKey).startsWith('PRIVATE_')) {
      fetchPrivatePropertyDetail({ listing_key: listingKey });
      return;
    }
    try {
      const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?listingKey=${listingKey}`, {});

      if (response.ok) {
        const data = await response.json();
        const properties = data.properties || [];
        if (properties.length > 0) {
          const property = properties[0];
          // Fetch full details using property ID
          if (property.id) {
            fetchPropertyDetail(property.id, property);
          }
        } else {
          console.error('No property found for listing key:', listingKey);
          alert('Property not found. Please check the listing key.');
        }
      } else {
        console.error('Failed to fetch property by listing key');
        alert('Failed to load property. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching property by listing key:', error);
      alert('Failed to load property. Please try again.');
    }
  };

  const fetchPropertyImage = async (listingKey) => {
    if (propertyImages[listingKey]) return propertyImages[listingKey];

    try {
      const imageUrl = propertyMediaUrls(await loadPropertyMedia(listingKey))[0];
      if (imageUrl) {
        setPropertyImages(prev => ({...prev, [listingKey]: imageUrl}));
        rememberPropertyPhoto(listingKey, imageUrl);
        return imageUrl;
      }
    } catch (error) {
      console.error('Error fetching property image:', error);
    }
    return null;
  };

  const fetchAllPropertyImages = async (listingKey, requestId = detailRequestRef.current, cover = '') => {
    try {
      const images = propertyMediaUrls(await loadPropertyMedia(listingKey));
      if (requestId !== detailRequestRef.current) return;
      if (images.length > 0) {
          const firstThree = images.slice(0, 3);
          setPropertyDetailImages(firstThree);
          setImagesLoading(false);
          rememberPropertyPhoto(listingKey, images[0]);
          if (images.length > 3) {
            setTimeout(() => {
              if (requestId === detailRequestRef.current) setPropertyDetailImages(images);
            }, 500);
          }
      } else {
        if (cover) setPropertyDetailImages([cover]);
        setImagesLoading(false);
      }
    } catch (error) {
      console.error('Error fetching all property images:', error);
      if (requestId === detailRequestRef.current) {
        if (cover) setPropertyDetailImages([cover]);
        setImagesLoading(false);
      }
    }
  };

  const closePropertyDetail = () => {
    detailRequestRef.current += 1;
    setPropertyDetail(null);
    setImagesLoading(false);
  };

  const displayedMapProperties = globeMap?.isSearchLens ? searchLensProperties : (globeMap?.properties || []);
  const propertyDetailOverlay = (
    <PropertyDetailModal
      propertyDetail={propertyDetail}
      propertyDetailImages={propertyDetailImages}
      imagesLoading={imagesLoading}
      onClose={() => {
        closePropertyDetail();
        if (directListingKey) navigate('/');
      }}
      user={user}
      isAdmin={isAdmin}
      likedProperties={likedProperties}
      setLikedProperties={setLikedProperties}
      senderEmail={senderEmail}
      senderPassword={senderPassword}
      onLeadCaptured={({ name: capturedName, email: capturedEmail, phone }) => {
        setName(capturedName);
        setEmail(capturedEmail);
        setPhoneNumber(phone);
      }}
    />
  );



  useEffect(() => {
    const pending = pendingPropertyRevealRef.current;
    if (!pending) return undefined;
    const latest = [...messages].reverse().find(message => message.content === 'properties');
    if (!latest || latest.searchId !== pending.searchId || latest.pageError) {
      pendingPropertyRevealRef.current = null;
      return undefined;
    }
    if (latest.loadingMore) return undefined;
    const rail = assistantPropertyRailRef.current;
    const card = rail?.querySelector(`[data-property-index="${pending.index}"]`);
    if (!card) {
      pendingPropertyRevealRef.current = null;
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => {
      if (pendingPropertyRevealRef.current !== pending) return;
      pendingPropertyRevealRef.current = null;
      const mobile = window.matchMedia('(max-width: 820px)').matches;
      const panel = assistantResultsPanelRef.current;
      if (!panel || (mobile && assistantWorkspaceRef.current?.scrollLeft < 20)) return;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      if (document.activeElement?.closest('.assistant-results-load-more')) {
        card.querySelector('button')?.focus({ preventScroll: true });
      }
      if (mobile) {
        rail.scrollTo({ left: rail.scrollLeft + card.getBoundingClientRect().left - rail.getBoundingClientRect().left, behavior });
      } else {
        panel.scrollTo({ top: Math.max(0, panel.scrollTop + card.getBoundingClientRect().top - panel.getBoundingClientRect().top - 72), behavior });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [messages]);

  if (isGlobeMap) {
    return (
      <>
        <style>{`
          @media (max-width: 1024px) {
            .list-view-image, .list-view-map { width: 120px !important; height: 90px !important; }
            .list-view-house-icon { font-size: 24px !important; }
          }
          @media (max-width: 768px) {
            .list-view-image, .list-view-map { width: 100px !important; height: 75px !important; }
            .list-view-house-icon { font-size: 20px !important; }
          }
        `}</style>
      <div className={`full-page-map-container ${!globeMap ? 'filter-page' : ''} ${isDarkMode ? 'dark-mode' : ''}`}>
        {!globeMap && initialCityLoading ? (
          <div className="map-location-form" style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'60vh'}}>
            <div style={{width:40,height:40,border:'3px solid rgba(255,255,255,0.2)',borderTop:'3px solid #fff',borderRadius:'50%',animation:'spin 0.8s linear infinite',marginBottom:16}}></div>
            <p style={{fontSize:'1.1rem',opacity:0.85}}>Loading {initialCity} properties...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : !globeMap ? (
          <div className="map-location-form">
            <button
              className="close-map-page"
              onClick={() => onGlobeMapClose()}
              aria-label={tr('Close property search and return home', 'Cerrar la búsqueda y volver al inicio')}
            >
              ← {tr('Back to Main', 'Volver al inicio')}
            </button>
            <header className="property-search-heading"><span>Live property search</span><h2>Find the right property</h2><p>Start with a location and refine what matters. Saved-search alerts are optional after you view results.</p></header>
            {POPULAR_SEARCH_AREAS.length > 0 && (
              <div className="property-search-popular" aria-label="Popular property searches">
                <span>Popular:</span>
                {POPULAR_SEARCH_AREAS.map((area) => (
                  <button type="button" key={area} onClick={() => setLocationInput(area)}>{area}</button>
                ))}
              </div>
            )}
            {likedProperties.size > 0 && (
              <section className="property-saved-homes" aria-labelledby="saved-homes-heading">
                <span className="property-saved-homes-icon" aria-hidden="true">♥</span>
                <div>
                  <h3 id="saved-homes-heading">Your saved homes</h3>
                  <p>{likedProperties.size} {likedProperties.size === 1 ? 'property is' : 'properties are'} saved in this browser.</p>
                  {savedHomesError && <small role="alert">{savedHomesError}</small>}
                </div>
                <SavedPropertyLists onView={handleViewSavedHomes} busy={savedHomesLoading} error={savedHomesError} />
              </section>
            )}
            <form onChange={()=>{sharedSearchRef.current=null;}} id="property-search-form" onSubmit={handleGlobeMapSubmit}>
              <div className="filter-row">
                <input type="text" placeholder={searchLensEnabled ? `Optional. Defaults to ${getDefaultSearchLensLabel()}` : 'Enter city, state, or zip'} value={locationInput} onChange={e => setLocationInput(e.target.value)} aria-label="City, state, or ZIP code" className="location-filter-input" required={!searchLensEnabled && !listingKey} />
                <select aria-label="Property type" className="filter-select" value={propertyType} onChange={e => {
                  setPropertyType(e.target.value);
                  setPropertySubType('');
                }}>
                  <option value="Residential">Residential</option>
                  <option value="CommercialSale">Commercial Sale</option>
                  <option value="ResidentialLease">Residential Lease</option>
                  <option value="Land">Land</option>
                  <option value="ResidentialIncome">Residential Income</option>
                  <option value="CommercialLease">Commercial Lease</option>
                  <option value="Farm">Farm</option>
                </select>
              </div>
              <div className="filter-row">
                <input type="number" aria-label="Minimum price" placeholder="Min Price" value={minPrice} onChange={e => setMinPrice(e.target.value)} className="filter-select" />
                <input type="number" aria-label="Maximum price" placeholder="Max Price" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} className="filter-select" />
              </div>
              <div className="filter-row"><select aria-label="Minimum bedrooms" className="filter-select" value={minBedrooms} onChange={e => setMinBedrooms(e.target.value)}>
                  <option value="">Min Bedrooms</option>
                  <option value="1">1+</option>
                  <option value="2">2+</option>
                  <option value="3">3+</option>
                  <option value="4">4+</option>
                </select></div>
              <SearchFilterPanel busy={isGlobeMapLoading} count={additionalFilterCount} onApply={() => document.getElementById('property-search-form')?.requestSubmit()}><div className="filter-row"><select className="filter-select" value={maxBedrooms} onChange={e => setMaxBedrooms(e.target.value)}>
                    <option value="">Max Bedrooms</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5+</option>
                  </select></div>
                <label className={`search-lens-card ${searchLensEnabled ? 'active' : ''}`}>
                  <input type="checkbox" checked={searchLensEnabled} onChange={e => setSearchLensEnabled(e.target.checked)} />
                  <span className="search-lens-card-mark" aria-hidden="true">⌖</span>
                  <span className="search-lens-card-copy">
                    <span>Search Lens by Agent Winds</span>
                    <small>Explore the map with a movable lens and load only homes inside the circle.</small>
                  </span>
                </label>
                <div className="filter-row">
                  <select className="filter-select" value={propertySubType} onChange={e => setPropertySubType(e.target.value)}>
                    <option value="">Any home type</option>
                    <option value="SingleFamilyResidence">Single Family</option>
                    <option value="Apartment">Apartment</option>
                    <option value="Condominium">Condominium</option>
                    <option value="Townhouse">Townhouse</option>
                    <option value="Duplex">Duplex</option>
                    <option value="Ranch">Ranch</option>
                    <option value="Other">Other</option>
                  </select>
                  <select className="filter-select" value={priceSort || (listingDateSort === 'newold' ? 'listnewold' : listingDateSort === 'oldnew' ? 'listoldnew' : '')} onChange={e => {
                    const val = e.target.value;
                    if (val === 'lowhigh' || val === 'highlow') {
                      setPriceSort(val);
                      setListingDateSort('');
                    } else if (val === 'listnewold' || val === 'listoldnew') {
                      setListingDateSort(val === 'listnewold' ? 'newold' : 'oldnew');
                      setPriceSort('');
                    }
                  }}>
                    <option value="">Sort By</option>
                    <option value="lowhigh">Price: Low to High</option>
                    <option value="highlow">Price: High to Low</option>
                    <option value="listnewold">Listed Date: Newest</option>
                    <option value="listoldnew">Listed Date: Oldest</option>
                  </select>
                </div>
                <div className="filter-row">
                  <input type="number" placeholder="Min Sq Ft" value={minSqft} onChange={e => setMinSqft(e.target.value)} className="filter-select" />
                  <input type="number" placeholder="Max Sq Ft" value={maxSqft} onChange={e => setMaxSqft(e.target.value)} className="filter-select" />
                </div>
                <div className="filter-row">
                  <input type="number" placeholder="Stories" value={stories} onChange={e => setStories(e.target.value)} className="filter-select" />
                  <select className="filter-select" value={foundation} onChange={e => setFoundation(e.target.value)}>
                    <option value="">Foundation Type</option>
                    <option value="CrawlSpace">Crawl Space</option>
                    <option value="Slab">Slab</option>
                  </select>
                </div>
                <div className="filter-row filter-year-row">
                  <input type="number" placeholder="Min Year Built" value={minYearBuilt} onChange={e => setMinYearBuilt(e.target.value)} className="filter-select" />
                  <input type="number" placeholder="Max Year Built" value={maxYearBuilt} onChange={e => setMaxYearBuilt(e.target.value)} className="filter-select" />
                </div>
                <div className="filter-row">
                  <input type="text" placeholder="School District" value={schoolDistrict} onChange={e => setSchoolDistrict(e.target.value)} className="filter-select" />
                  <input type="text" placeholder="Listing Key" value={listingKey} onChange={e => setListingKey(e.target.value)} className="filter-select" />
                </div>
                <div className="filter-row">
                  <select className="filter-select" value={acreage} onChange={e => setAcreage(e.target.value)}>
                    <option value="">Acreage</option>
                    <option value="<5">&lt; 5 Acres</option>
                    <option value="5to10">5 to 10 Acres</option>
                    <option value="10+">10+ Acres</option>
                  </select>
                  <select className="filter-select" multiple value={communityFeatures} onChange={e => setCommunityFeatures(Array.from(e.target.selectedOptions, o => o.value))}>
                    <option value="greenbelt">Greenbelt</option>
                    <option value="hill">Hill Country</option>
                    <option value="golf">Golf Course</option>
                    <option value="downtown">Downtown</option>
                    <option value="water">Water</option>
                  </select>
                </div>
                <RobustSearchControls expanded filters={advancedFilters} onChange={next => {sharedSearchRef.current=null;setAdvancedFilters(next);}} scope={{state:advancedFilters.state || SITE_CONFIG.SEARCH_STATES || SITE_CONFIG.STATE,propertyType}} />
                <div className="filter-row">
                  <input type="number" placeholder="Min Bathrooms" value={minBathrooms} onChange={e => setMinBathrooms(e.target.value)} className="filter-select" />
                  <input type="number" placeholder="Max Bathrooms" value={maxBathrooms} onChange={e => setMaxBathrooms(e.target.value)} className="filter-select" />
                </div>
                <div className="filter-row filter-toggle-row">
                  <label className="agent-toggle">
                    <input type="checkbox" checked={showAgentListings} onChange={e => setShowAgentListings(e.target.checked)} />
                              Show only {SITE_CONFIG.AGENT.NAME}'s listings
                            </label>
                  <label className="agent-toggle">
                    <input type="checkbox" checked={hasFence} onChange={e => setHasFence(e.target.checked)} />
                              Has Fencing
                            </label>
                  <label className="agent-toggle">
                    <input type="checkbox" checked={hasPool} onChange={e => setHasPool(e.target.checked)} />
                              Pool
                            </label>
                </div></SearchFilterPanel>
              {manualSearchError && <p role="alert">{manualSearchError}</p>}
              <button type="submit" className="full-page-search-btn" aria-label="Search for properties with selected filters" aria-busy={isGlobeMapLoading} disabled={isGlobeMapLoading}>
                {isGlobeMapLoading ? <><span className="property-search-spinner" aria-hidden="true" /> Loading properties…</> : 'Search Properties'}
              </button>
            </form>
            <footer className="property-search-utility">
              <span>Need help deciding where to start?</span>
              <a href="/guides">Explore local guides</a>
              <a href={`mailto:${SITE_CONFIG.AGENT.EMAIL}`}>Email {SITE_CONFIG.AGENT.NAME.split(' ')[0]}</a>
              <a href={`tel:${SITE_CONFIG.AGENT.PHONE}`}>Call</a>
            </footer>
          </div>
        ) : (
          <div className={[`full-page-leaflet-container ${globeMap.isSearchLens ? 'search-lens-mode' : ''}`,mapView && hybridView ? 'hybrid-map-view' : '',mapTrayExpanded ? 'map-tray-expanded' : ''].join(' ')}>
<div className="map-workspace">
            <button
              className="close-map-page"
              onClick={() => {
                if (enlargedImage) {
                  setEnlargedImage(null);
                } else {
                  setGlobeMap(null);initialMapRequestRef.current++;initialMapAbortRef.current?.abort();sessionStorage.removeItem('globeMapData');window.history.replaceState(window.history.state,'','/search');sharedSearchRef.current=null;
                  alertTriggeredRef.current = false;
                  setShowAlertPill(false);
                  setAlertPillState('prompt');
                  setSearchLensProperties([]);
                  setSearchLensLoading(false);
                  setSearchLensResultCount(0);
                  setSearchLensRadiusMiles(null);
                  setSearchLensPosition(null);
                  setSearchLensSize(getDefaultSearchLensSize());
                  setSearchLensDragging(false);
                  setSearchLensPreview(null);
                  searchLensParamsRef.current = null;
                  clearTimeout(searchLensDebounceRef.current);
                  if (window.location.pathname === '/search') {
                    window.history.replaceState(null, '', '/');
                  }
                }
              }}
              aria-label="Go back to property search filters"
            >
              ← Back to Filters
            </button>

            {mapView && globeMap.mapData && (mapStats.loading||mapStats.error||globeMap.privateUnavailable) && <p className="bounded-map-status" role={mapStats.error?'alert':'status'}>{mapStats.error||(mapStats.loading?'Updating visible homes…':'Private listings are currently unavailable.')}</p>}
            {mapView && <div ref={globeMapRef} className="full-page-leaflet-map"></div>}
            {mapView && globeMap.isSearchLens && searchLensPosition && (
              <>
                <div className="search-lens-overlay" aria-hidden="true">
                  <div
                    className={`search-lens-circle ${searchLensDragging ? 'dragging' : ''}`}
                    style={{
                      left: `${searchLensPosition.x}px`,
                      top: `${searchLensPosition.y}px`,
                      width: `${getLensDiameter()}px`,
                      height: `${getLensDiameter()}px`
                    }}
                  />
                  <button
                    type="button"
                    className={`search-lens-handle ${searchLensDragging ? 'dragging' : ''}`}
                    style={{
                      left: `${searchLensPosition.x}px`,
                      top: `${searchLensPosition.y - (getLensDiameter() / 2) - 12}px`
                    }}
                    onMouseDown={handleSearchLensDragStart}
                    onTouchStart={handleSearchLensDragStart}
                    aria-label="Move Search Lens"
                  >
                    Move Lens
                  </button>
                </div>
                {searchLensPreview && (
                  <div
                    className="search-lens-hover-preview"
                    style={{
                      left: `${searchLensPreview.x}px`,
                      top: `${searchLensPreview.y - 14}px`
                    }}
                  >
                    <img src={searchLensPreview.imageUrl} alt="" />
                  </div>
                )}
                <div className={`search-lens-panel ${searchLensControlsOpen ? 'controls-open' : 'controls-collapsed'}`}>
                  <button
                    type="button"
                    className="search-lens-summary"
                    onClick={() => setSearchLensControlsOpen(open => !open)}
                    aria-expanded={searchLensControlsOpen}
                  >
                    <div className="search-lens-summary-text">
                      <div className="search-lens-title">Search Lens by Agent Winds</div>
                      <div className="search-lens-meta">
                        {searchLensLoading
                          ? 'Searching lens...'
                          : `${searchLensResultCount} ${searchLensResultCount === 1 ? 'home' : 'homes'} in lens`}
                        {searchLensRadiusMiles && (
                          <span>{searchLensRadiusMiles.toFixed(2)} mi radius</span>
                        )}
                      </div>
                    </div>
                    <span className="search-lens-summary-chevron" aria-hidden="true">
                      {searchLensControlsOpen ? '▾' : '▸'}
                    </span>
                  </button>
                  <div className="search-lens-controls">
                  <div className="search-lens-size-control" aria-label="Search Lens size">
                    {['small', 'medium', 'large'].map(size => (
                      <button
                        key={size}
                        type="button"
                        className={searchLensSize === size ? 'active' : ''}
                        onClick={() => handleSearchLensSizeChange(size)}
                      >
                        {size.charAt(0).toUpperCase() + size.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div className="search-lens-filter-control">
                    <label>
                      <span>
                        Max Price
                        <strong>{maxPrice ? `$${Number(maxPrice).toLocaleString()}` : 'Any'}</strong>
                      </span>
                      <input
                        type="range"
                        min="0"
                        max="2000000"
                        step="25000"
                        value={maxPrice || '0'}
                        onChange={(e) => handleSearchLensMaxPriceChange(e.target.value)}
                      />
                    </label>
                    <label>
                      <span>
                        Rooms
                        <strong>{minBedrooms ? `${minBedrooms}+` : 'Any'}</strong>
                      </span>
                      <input
                        type="range"
                        min="0"
                        max="6"
                        step="1"
                        value={minBedrooms || '0'}
                        onChange={(e) => handleSearchLensBedroomsChange(e.target.value)}
                      />
                    </label>
                  </div>
                  </div>
                  <button
                    type="button"
                    className="search-lens-back-btn"
                    onClick={() => {
                      setGlobeMap(null);
                      setSearchLensProperties([]);
                      setSearchLensLoading(false);
                      setSearchLensResultCount(0);
                      setSearchLensRadiusMiles(null);
                      setSearchLensPosition(null);
                      setSearchLensSize(getDefaultSearchLensSize());
                      setSearchLensPreview(null);
                      searchLensParamsRef.current = null;
                      clearTimeout(searchLensDebounceRef.current);
                    }}
                  >
                    Back to Filters
                  </button>
                </div>
              </>
            )}
            {!mapView && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                padding: '80px 20px 100px 20px',
                background: isDarkMode ? 'rgb(15, 23, 42)' : '#f3f3f3',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                  {globeMap.editingListId && <section className="saved-list-edit-heading"><h3>Add homes to {collections.find(row=>row.id===globeMap.editingListId)?.name || 'your list'}</h3><p>Check the saved homes you want in this list. Your choices are saved immediately.</p><button type="button" onClick={()=>{const list=collections.find(row=>row.id===globeMap.editingListId);if(list?.listingKeys.length)handleViewSavedHomes(list.listingKeys,list.name,list.id);else setGlobeMap(null);}}>Done</button></section>}{displayedMapProperties && displayedMapProperties.slice(listViewPage * 7, (listViewPage + 1) * 7).map((property, index) => {
                  return (
                    <div
                      key={index}
                      onClick={() => property.isPrivate ? fetchPrivatePropertyDetail(property) : fetchPropertyDetail(property.id, property)}
                      className={`list-view-card ${globeMap.isSavedHomes ? 'saved-home-card' : ''}`}
                      style={{
                        padding: '20px',
                        marginBottom: '16px',
                        border: 'none',
                        borderLeft: '3px solid transparent',
                        cursor: 'pointer',
                        background: 'white',
                        display: 'flex',
                        gap: '20px',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        borderRadius: '4px',
                        maxWidth: '900px',
                        width: '100%'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderLeftColor = '#0176d3';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                        prefetchPropertyDetail(property);
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderLeftColor = 'transparent';
                        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                      }}
                    >
                      <script type="application/ld+json">
                        {JSON.stringify({
                          "@context": "https://schema.org",
                          "@type": "RealEstateProperty",
                          "name": formatPropertyAddress(property),
                          "address": {
                            "@type": "PostalAddress",
                            "streetAddress": formatStreetAddress(property),
                            "addressLocality": property.city,
                            "addressRegion": property.state,
                            "postalCode": property.postal_code
                          },
                          "price": property.current_price || property.price,
                          "priceCurrency": "USD",
                          "numberOfRooms": property.bedrooms_total,
                          "floorSize": {
                            "@type": "QuantitativeValue",
                            "value": property.living_area,
                            "unitCode": "SQF"
                          },
                          "propertyType": property.property_type,
                          "image": property.first_photo_url || property.image_url || property.photo_url,
                          "url": `${window.location.origin}/listing/${property.listing_key}`
                        })}
                      </script>
                      <PropertyThumbnail
                        listingKey={property.listing_key}
                        src={property.first_photo_url || property.image_url || property.photo_url}
                        alt={`Main exterior of property at ${property.address || property.street_address}`}
                        className="list-view-image"
                        style={{width: '200px', height: '150px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0, border: '1px solid #e5e5e5'}}
                      />
                      <div style={{flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                        <div style={{fontWeight: '600', fontSize: '24px', color: '#0176d3', marginBottom: '8px'}}>
                          ${property.current_price?.toLocaleString() || property.price}
                        </div>
                        <div style={{fontSize: '16px', color: '#ffffff', marginBottom: '6px', fontWeight: '400'}}>
                          {formatPropertyAddress(property)}
                        </div>
                        {property.property_type && (
                          <div style={{fontSize: '14px', color: '#706e6b', marginBottom: '4px'}}>
                            {property.property_type}
                          </div>
                        )}
                        {property.bedrooms_total && (
                          <div style={{fontSize: '14px', color: '#706e6b'}}>
                            🛏️ {property.bedrooms_total} bed
                          </div>
                        )}
                      </div>
                      {property.latitude && property.longitude && (
                        <div
                          className="list-view-map"
                          style={{position: 'relative', width: '200px', height: '150px', flexShrink: 0, cursor: 'pointer', borderRadius: '4px', overflow: 'hidden', border: '1px solid #e5e5e5'}}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedMap({lat: property.latitude, lon: property.longitude, address: formatPropertyAddress(property)});
                          }}
                        >
                <img
                  src={`https://tile.openstreetmap.org/15/${getTileCoords(property.latitude, property.longitude, 15).x}/${getTileCoords(property.latitude, property.longitude, 15).y}.png`}
                  alt={`Map location for property at ${property.address || property.street_address}`}
                  style={{width: '100%', height: '100%', objectFit: 'cover'}}
                />
                          <div className="list-view-house-icon" style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '28px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'}}>🏠</div>
                        </div>
                      )}
                    {globeMap.isSavedHomes && (globeMap.editingListId ? <PropertyListMembership listingKey={property.listing_key} listId={globeMap.editingListId}/> : <PropertyListPicker listingKey={property.listing_key}/>)}</div>
                  );
                })}
                <div style={{display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px', paddingBottom: '20px', alignItems: 'center'}}>
                  {listViewPage > 0 && (
                    <button
                      onClick={() => setListViewPage(listViewPage - 1)}
                      style={{
                        padding: '8px 16px',
                        background: 'white',
                        color: '#0176d3',
                        border: '1px solid #dddbda',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f3f3f3';
                        e.currentTarget.style.borderColor = '#0176d3';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#dddbda';
                      }}
                    >
                      ← Previous
                    </button>
                  )}
                  <span style={{padding: '8px 16px', fontSize: '13px', color: '#3e3e3c', fontWeight: '400'}}>
                    Page {listViewPage + 1} of {Math.ceil(displayedMapProperties.length / 7)}
                  </span>
                  {(listViewPage + 1) * 7 < displayedMapProperties.length && (
                    <button
                      onClick={() => setListViewPage(listViewPage + 1)}
                      style={{
                        padding: '8px 16px',
                        background: 'white',
                        color: '#0176d3',
                        border: '1px solid #dddbda',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f3f3f3';
                        e.currentTarget.style.borderColor = '#0176d3';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#dddbda';
                      }}
                    >
                      Next →
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="map-toolbar map-toolbar--compact"><div className="map-info">
 <div className="map-view-switcher" role="group" aria-label="Property view">
 <button type="button" className="map-view-option" aria-pressed={!hybridView} onClick={() => {setMapView(true);setMapTrayExpanded(false);setHybridView(false);setActiveMapTool(null);}}>Map</button>
 <button type="button" className="map-view-option map-view-toggle" aria-pressed={hybridView} onClick={() => {setMapView(true);setMapTrayExpanded(false);setHybridView(true);setActiveMapTool(null);}}>Hybrid</button>
 <button type="button" className="map-view-option map-list-option" onClick={()=>{const filters={...appliedSearchRef.current,...(globeMap.mapData?mapStats.bounds:{})};navigate(`/properties/list?${searchQuery(filters)}`,{state:{user,searchFilters:filters,...(globeMap.isSavedHomes?{properties:globeMap.properties,isSavedHomes:true,savedMap:globeMap}:{})}});}}>List</button>
 </div><div className="map-tools-row"><div className="map-result-count" aria-live="polite">{globeMap.mapData?`${mapStats.count??globeMap.mapData.match_count} homes in view`:globeMap.isSearchLens?`${searchLensResultCount} properties in Search Lens`:globeMap.location}</div><div className="map-tools-actions">{showAlertPill && mapView && !manualSearchError && (
              <AlertPill compact compactOpen={activeMapTool === 'alerts'} onCompactOpenChange={setAlertsOpen}
                city={globeMap.location}
                pillState={alertPillState}
                setPillState={setAlertPillState}
                searchFilters={appliedSearchRef.current}
                existingUser={{
                  name: user?.username || name || '',
                  email: user?.email || email || '',
                  phoneNumber: user?.phoneNumber || phoneNumber || '',
                  postalCode: zipCode || '',
                }}
                onClose={() => setShowAlertPill(false)}
              />
            )}</div></div>
 </div></div>
          </div>
<MapPlaceLayers compact compactOpen={activeMapTool === 'places'} onCompactOpenChange={setPlacesOpen} map={mapView && !propertyDetail && !expandedMap ? hybridMapInstance : null} />
{mapView && hybridView && !propertyDetail && <MapPropertyGrid favorites={favorites} onSave={property=>toggleFavorite(property,user)} map={hybridMapInstance} trayExpanded={mapTrayExpanded} onTrayToggle={()=>setMapTrayExpanded(value=>!value)} params={globeMap.searchParams || globeMap.searchFilters || {}} properties={!globeMap.mapData ? globeMap.properties : undefined} onProperty={property=>property.isPrivate?fetchPrivatePropertyDetail(property):fetchPropertyDetail(property.id,property)} />}
</div>
        )}
        {propertyDetailOverlay}
        {expandedMap && isGlobeMap && (
          <div className="expanded-map-modal" onClick={() => setExpandedMap(null)}>
            <div className="expanded-map" onClick={(e) => e.stopPropagation()}>
              <button className="close-expanded-map" onClick={() => setExpandedMap(null)}>
                ×
              </button>
              <div ref={mapRef} className="leaflet-map"></div>
              <MapPlaceLayers map={placesExpandedMapInstance} />
            </div>
          </div>
        )}
        <div className={(`mls-attribution-bar ${!globeMap ? 'filter-page' : ''}`) + " mobile-map-disclosure"}>
          <img src={SITE_CONFIG.MLS.LOGO_URL} alt={SITE_CONFIG.MLS.SHORT_NAME} />
          <span><MlsDisclosure variant="preview" showLicenses /></span>
        </div>
      </div>
      </>
    );
  }

  const latestPropertyMessageIndex = messages.reduce(
    (latestIndex, message, index) => message.content === 'properties' ? index : latestIndex,
    -1
  );
  const latestPropertyMessage = latestPropertyMessageIndex >= 0 ? messages[latestPropertyMessageIndex] : null;
  const visibleWorkspaceProperties = latestPropertyMessage
    ? latestPropertyMessage.properties.slice(0, ((latestPropertyMessage.currentPage || 0) + 1) * itemsPerPage)
    : [];
  const hasPropertyWorkspace = isFullPage && visibleWorkspaceProperties.length > 0;
  const remainingWorkspaceProperties = latestPropertyMessage
    ? Math.max(0, latestPropertyMessage.properties.length - visibleWorkspaceProperties.length) + (latestPropertyMessage.hasMore ? itemsPerPage : 0)
    : 0;
  const nextWorkspacePropertyCount = Math.min(itemsPerPage, remainingWorkspaceProperties);
  const hasConversationStarted = Boolean(
    selectedService ||
    messages.length ||
    showThankYouMessage ||
    showRegistration ||
    showLogin ||
    showForgotPassword ||
    isThinking ||
    isPropertySearching
  );
  const assistantName = tr(`${SITE_CONFIG.AGENT.NAME} Assistant`, `Asistente de ${SITE_CONFIG.AGENT.NAME}`);
  const assistantActivity = isPropertySearching ? tr('Searching live listings', 'Buscando propiedades disponibles') : isThinking ? tr('Thinking', 'Pensando') : tr('Ready to help', 'Listo para ayudar');
  const formatPropertyPrice = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : value;
  };
  const getPropertyAddress = (property) => formatPropertyAddress(property);
  const getPropertyKey = (property, index) => property.listing_key || property.id || index;
  const toggleWorkspaceFavorite = (property) => toggleFavorite(property, user);

  const handleWorkspaceShowMore = () => {
    if (latestPropertyMessage.loadingMore) return;
    pendingPropertyRevealRef.current = {
      searchId: latestPropertyMessage.searchId,
      index: visibleWorkspaceProperties.length
    };
    handleShowMoreProperties(latestPropertyMessageIndex);
  };

  return (
    <div className={`action-buttons-container ${isFullPage ? 'assistant-full-page' : ''} ${hasPropertyWorkspace ? 'assistant-has-results' : ''} ${hasConversationStarted ? 'assistant-conversation-started' : ''}`} role="region" aria-label={tr('Real estate chat assistant', 'Asistente inmobiliario por chat')}>
      <div className={`chat-box assistant-shell ${isDarkMode ? 'dark-mode' : ''}`} role="complementary">
        {isFullPage && (
          <header className="assistant-header">
            <div className="assistant-header-primary">
              {onBack && (
                <button type="button" className="assistant-header-back" onClick={onBack} aria-label={tr('Back to home', 'Volver al inicio')}>
                  <span aria-hidden="true">←</span>
                </button>
              )}
              <div className="assistant-header-avatar" aria-hidden="true">
                <span className="assistant-header-avatar-photo"><img src={SITE_CONFIG.LANDING_PAGE.AGENT_IMAGE} alt="" /></span>
                <span className="assistant-header-online"></span>
              </div>
              <div className="assistant-header-identity">
                <strong>{assistantName}</strong>
                <span><i className={isThinking || isPropertySearching ? 'assistant-status-busy' : ''}></i>{assistantActivity}</span>
              </div>
            </div>
            <div className="assistant-header-context">
              <LanguageToggle compact />
              <span className="assistant-live-listings-badge">{tr('Live Atlanta listings', 'Propiedades disponibles en Atlanta')}</span>
              {isAdmin && (
                <button type="button" className="assistant-admin-button" onClick={() => navigate('/admin')}>
                  <span aria-hidden="true">⚙</span> Admin
                </button>
              )}
              {user ? (
                <span className="assistant-user-state">{tr('Welcome', 'Bienvenido')}, {user.username || user.name || tr('back', 'de nuevo')}</span>
              ) : (
                <button type="button" className="assistant-account-button" onClick={() => { setShowRegistration(false); setShowForgotPassword(false); setShowLogin(true); }}>
                  {tr('Sign in', 'Iniciar sesión')}
                </button>
              )}
              <button type="button" className="assistant-new-chat" onClick={handleNewConversation}>{tr('New conversation', 'Nueva conversación')}</button>
            </div>
          </header>
        )}
        {hasPropertyWorkspace && (
          <nav className="assistant-mobile-workspace-tabs" aria-label="Switch between chat and property results">
            <button type="button" className={mobileWorkspaceView === 'chat' ? 'is-active' : ''} onClick={() => scrollMobileWorkspaceTo('chat')} aria-label="Return to chat conversation" aria-pressed={mobileWorkspaceView === 'chat'}>
              ← Chat
            </button>
            <button type="button" className={mobileWorkspaceView === 'results' ? 'is-active' : ''} onClick={() => scrollMobileWorkspaceTo('results')} aria-pressed={mobileWorkspaceView === 'results'}>
              Homes <span>{latestPropertyMessage.properties.length}</span>
            </button>
          </nav>
        )}
        <div className="assistant-workspace" ref={assistantWorkspaceRef} onScroll={handleMobileWorkspaceScroll}>
          <section className="assistant-conversation-pane" aria-label={tr('Conversation with Giovanni Assistant', 'Conversación con el asistente de Giovanni')}>
        <div className="chat-messages" ref={messagesContainerRef} role="log" aria-live="polite" aria-label={tr('Chat conversation history', 'Historial de la conversación')}>
          <div className="message bot-message welcome-message">
            <div className="assistant-welcome-kicker">
              <span></span>
              {tr('Your Atlanta real estate concierge', 'Su asesor inmobiliario en Atlanta')}
            </div>
            <div className="welcome-text">
              <h1>{tr('What can we help you', '¿Hacia dónde podemos ayudarle')} <em>{tr('move toward?', 'a avanzar?')}</em></h1>
              <p>{tr(`I'm ${assistantName}. I can search live properties, create alerts, help you plan a sale, or connect you directly with ${SITE_CONFIG.AGENT.NAME}.`, `Soy el ${assistantName}. Puedo buscar propiedades disponibles, crear alertas, ayudarle a planear una venta o conectarle directamente con ${SITE_CONFIG.AGENT.NAME}.`)}</p>
            </div>
            <div className="service-buttons assistant-intent-grid">
              <div className="button-row assistant-primary-intents">
                <button
                  className="service-button gold-outline"
                  onClick={() => handleServiceSelect('buying')}
                  aria-label={tr('Select buying service to search for properties to purchase', 'Seleccionar el servicio de compra para buscar propiedades')}
                >
                  <span className="assistant-intent-number">01</span>
                  <span><strong>{tr('Find a home', 'Buscar una casa')}</strong><small>{tr('Search live listings with guidance', 'Busque propiedades disponibles con orientación')}</small></span>
                  <span className="assistant-intent-arrow" aria-hidden="true">↗</span>
                </button>
                <button
                  className="service-button gold-outline"
                  onClick={() => handleServiceSelect('selling')}
                  aria-label={tr('Select selling service to list your property for sale', 'Seleccionar el servicio de venta para vender su propiedad')}
                >
                  <span className="assistant-intent-number">02</span>
                  <span><strong>{tr('Sell my home', 'Vender mi casa')}</strong><small>{tr('Share details and plan your next move', 'Comparta los detalles y planee su próximo paso')}</small></span>
                  <span className="assistant-intent-arrow" aria-hidden="true">↗</span>
                </button>
              </div>
              <div className="button-row assistant-secondary-intents">
                <button
                  className="service-button"
                  onClick={() => handleServiceSelect('lease')}
                  aria-label={tr('Select lease service to find rental properties', 'Seleccionar el servicio de alquiler para buscar propiedades')}
                >
                  {tr('Lease a home', 'Alquilar una casa')}
                </button>
                <button
                  className="service-button"
                  onClick={() => handlePromptSelect(tr('Help me create a property alert', 'Ayúdame a crear una alerta de propiedades'))}
                  aria-label={tr('Create a personalized property alert', 'Crear una alerta personalizada de propiedades')}
                >
                  {tr('Create an alert', 'Crear una alerta')}
                </button>
                <button
                  className="service-button"
                  onClick={() => handlePromptSelect(tr(`Contact ${SITE_CONFIG.AGENT.NAME}`, `Quiero contactar a ${SITE_CONFIG.AGENT.NAME}`))}
                  aria-label={tr(`Connect with ${SITE_CONFIG.AGENT.NAME}`, `Contactar a ${SITE_CONFIG.AGENT.NAME}`)}
                >
                  {tr('Talk to Giovanni', 'Hablar con Giovanni')}
                </button>
              </div>
            </div>
            <div className="assistant-welcome-trust">
              <span>{tr('Connected to live MLS data', 'Conectado a datos MLS actualizados')}</span>
              <span>{tr('Private and pressure-free', 'Privado y sin presión')}</span>
              <span>{tr('Human help whenever you want it', 'Ayuda personal cuando la necesite')}</span>
            </div>
          </div>

          {selectedService && !showThankYouMessage && (
            <div className="message bot-message assistant-guided-step">
              <div className="assistant-step-heading">
                <div>
                  <span>{selectedService === 'selling' ? tr('Seller consultation', 'Consulta para vendedores') : selectedService === 'lease' ? tr('Lease search', 'Búsqueda de alquiler') : tr('Buyer search', 'Búsqueda para compradores')}</span>
                  <h3 className="form-title">
                    {serviceStep === 0 && (selectedService === 'selling' ? tr('Where is the property?', '¿Dónde está la propiedad?') : tr('Where would you like to look?', '¿Dónde le gustaría buscar?'))}
                    {serviceStep === 1 && tr('What should I call you?', '¿Cómo le gustaría que le llamara?')}
                    {serviceStep === 2 && tr('Where should I send your matches?', '¿Dónde debo enviarle las propiedades que coincidan?')}
                    {serviceStep === 3 && tr('What is the best number for you?', '¿Cuál es el mejor número para comunicarnos?')}
                  </h3>
                </div>
                <span className="assistant-step-count">{serviceStep + 1} / 4</span>
              </div>
              <div className="assistant-step-progress" aria-hidden="true"><span style={{width: `${((serviceStep + 1) / 4) * 100}%`}}></span></div>
              <p className="form-subtitle">
                {serviceStep === 0 && (selectedService === 'selling' ? tr('A street address or neighborhood is perfect.', 'Una dirección o un vecindario es suficiente.') : tr('Enter an Atlanta-area city, neighborhood, or ZIP code.', 'Ingrese una ciudad, vecindario o código postal del área de Atlanta.'))}
                {serviceStep === 1 && tr('I’ll use this to personalize the conversation.', 'Usaré esta información para personalizar la conversación.')}
                {serviceStep === 2 && tr('Your email lets me save the search and send new matches if you choose.', 'Su correo me permite guardar la búsqueda y enviarle nuevas opciones si así lo desea.')}
                {serviceStep === 3 && tr(`Only ${SITE_CONFIG.AGENT.NAME}'s team will receive your information.`, `Solo el equipo de ${SITE_CONFIG.AGENT.NAME} recibirá su información.`)}
              </p>
              <form className="chat-form assistant-step-form" onSubmit={handleServiceStepContinue}>
                <HoneypotField />
                {serviceStep === 0 && (
                  <input type="text" placeholder={tr('City, neighborhood, or ZIP code', 'Ciudad, vecindario o código postal')} value={zipCode} onChange={(e) => setZipCode(e.target.value)} className="form-input" autoFocus required />
                )}
                {serviceStep === 1 && (
                  <input type="text" placeholder={tr('Your name', 'Su nombre')} value={name} onChange={(e) => setName(e.target.value)} className="form-input" autoFocus required />
                )}
                {serviceStep === 2 && (
                  <input type="email" placeholder={tr('Email address', 'Correo electrónico')} value={email} onChange={(e) => setEmail(e.target.value)} className="form-input" autoFocus required />
                )}
                {serviceStep === 3 && (
                  <input type="tel" placeholder={tr('Phone number', 'Número de teléfono')} value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} className="form-input" autoFocus required />
                )}
                <div className="assistant-step-actions">
                  {serviceStep > 0 && <button type="button" className="assistant-step-back" onClick={() => setServiceStep((step) => step - 1)}>{tr('Back', 'Atrás')}</button>}
                  <button type="submit" className="form-submit cta-button" aria-label={serviceStep === 3 ? tr('Submit your information and continue', 'Enviar su información y continuar') : tr('Continue to the next question', 'Continuar a la siguiente pregunta')}>
                    {serviceStep === 3 ? tr('Continue', 'Continuar') : tr('Next', 'Siguiente')} <span aria-hidden="true">→</span>
                  </button>
                </div>
              </form>
              <div className="assistant-step-summary">
                {zipCode && serviceStep > 0 && <span><small>{tr('Area', 'Área')}</small>{zipCode}</span>}
                {name && serviceStep > 1 && <span><small>{tr('Name', 'Nombre')}</small>{name}</span>}
                {email && serviceStep > 2 && <span><small>{tr('Email', 'Correo')}</small>{email}</span>}
              </div>
            </div>
          )}
          {showThankYouMessage && (
            <div className="message bot-message">
              {selectedService === 'buying' ? (
                <div>
                  <p>{tr(`Thank you, ${name}! Since you're looking to buy, would you like to see some properties for sale in ${zipCode}?`, `¡Gracias, ${name}! Ya que desea comprar, ¿le gustaría ver propiedades en venta en ${zipCode}?`)}</p>
                  <div className="service-buttons">
                    <button className="service-button" onClick={() => { console.log('Fetching properties for zip:', zipCode); fetchProperties({zip: zipCode}); }}>{tr('Yes, Show Properties', 'Sí, mostrar propiedades')}</button>
                    <button className="service-button" onClick={() => setShowThankYouMessage(false)}>{tr('Not Right Now', 'Ahora no')}</button>
                  </div>
                </div>
              ) : selectedService === 'selling' ? (
                <div>
                  <p>{tr(`Thank you, ${name}! Let's get some details about your property to help with the selling process.`, `¡Gracias, ${name}! Comparta algunos detalles de su propiedad para ayudarle con el proceso de venta.`)}</p>
                  <form className="chat-form" onSubmit={handleSellingFormSubmit}>
                <HoneypotField />
                    <input
                      type="text"
                      placeholder={tr('Property location/address', 'Ubicación o dirección de la propiedad')}
                      value={propertyLocation}
                      onChange={(e) => setPropertyLocation(e.target.value)}
                      className="form-input"
                      required
                    />
                    <input
                      type="text"
                      placeholder={tr('Property size (e.g., 3 bed, 2 bath)', 'Tamaño (por ejemplo, 3 habitaciones, 2 baños)')}
                      value={propertySize}
                      onChange={(e) => setPropertySize(e.target.value)}
                      className="form-input"
                      required
                    />
                    <select
                      value={propertyCondition}
                      onChange={(e) => setPropertyCondition(e.target.value)}
                      className="form-input"
                      required
                    >
                      <option value="">{tr('Select property condition', 'Seleccione el estado de la propiedad')}</option>
                      <option value="excellent">{tr('Excellent', 'Excelente')}</option>
                      <option value="good">{tr('Good', 'Bueno')}</option>
                      <option value="fair">{tr('Fair', 'Regular')}</option>
                      <option value="needs-work">{tr('Needs Work', 'Necesita reparaciones')}</option>
                    </select>
                    <select
                      value={sellingTimeline}
                      onChange={(e) => setSellingTimeline(e.target.value)}
                      className="form-input"
                      required
                    >
                      <option value="">{tr('Select timeline', 'Seleccione el plazo')}</option>
                      <option value="asap">{tr('ASAP', 'Lo antes posible')}</option>
                      <option value="1-3-months">{tr('1-3 months', '1-3 meses')}</option>
                      <option value="3-6-months">{tr('3-6 months', '3-6 meses')}</option>
                      <option value="6-months-plus">{tr('6+ months', 'Más de 6 meses')}</option>
                    </select>
                    <button type="submit" className="form-submit cta-button">{tr('Submit Property Details', 'Enviar detalles de la propiedad')}</button>
                  </form>
                </div>
              ) : selectedService === 'lease' ? (
                <p>{tr(`Thank you, ${name}! How else can I help you today?`, `¡Gracias, ${name}! ¿En qué más puedo ayudarle hoy?`)}</p>
              ) : (
                <p>{tr(`Thank you, ${name}! How else can I help you today?`, `¡Gracias, ${name}! ¿En qué más puedo ayudarle hoy?`)}</p>
              )}
            </div>
          )}
          {messages.map((message, index) => (
            <div key={index} className={`message ${message.type === 'user' ? 'user-message' : 'bot-message'} ${message.content === 'properties' ? 'property-results-message' : ''}`}>
              {message.type !== 'user' && message.content !== 'properties' && <span className="assistant-message-author">{assistantName}</span>}
              {message.content === 'properties' ? (
                <div>
                  {message.properties.length > 0
                    ? tr(`Found ${message.properties.length} properties:`, `Encontré ${message.properties.length} propiedades:`)
                    : message.privateUnavailable
                      ? tr('No MLS properties matched this search.', 'Ninguna propiedad del MLS coincide con esta búsqueda.')
                      : tr('No properties matched this search. Try changing a feature, budget, or location.', 'No hay propiedades que coincidan. Pruebe cambiar una característica, el presupuesto o la ubicación.')}
                  {message.privateUnavailable && (
                    <p role="status">{tr('Private listings could not be loaded. These results include MLS listings only.', 'No se pudieron cargar las propiedades privadas. Estos resultados solo incluyen propiedades del MLS.')}</p>
                  )}
                  {message.properties.length > itemsPerPage && (
                    <div style={{fontSize: '14px', color: '#666', marginBottom: '10px'}}>
                      {tr(`Showing ${Math.min(((message.currentPage || 0) + 1) * itemsPerPage, message.properties.length)} of ${message.properties.length} properties`, `Mostrando ${Math.min(((message.currentPage || 0) + 1) * itemsPerPage, message.properties.length)} de ${message.properties.length} propiedades`)}
                    </div>
                  )}
                  <div className="properties-list">
                    {message.properties.slice(0, ((message.currentPage || 0) + 1) * itemsPerPage).map((property, propIndex) => (
                      <div key={propIndex} className="property-item" role="article" aria-label={tr(`Property listing: ${property.address || property.street_address || 'Property'} - $${property.current_price || property.price}`, `Propiedad: ${property.address || property.street_address || 'Propiedad'} - $${property.current_price || property.price}`)}>
                        <script type="application/ld+json">
                          {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "RealEstateProperty",
                            "name": formatPropertyAddress(property),
                            "address": {
                              "@type": "PostalAddress",
                              "streetAddress": formatStreetAddress(property),
                              "addressLocality": property.city,
                              "addressRegion": property.state,
                              "postalCode": property.postal_code
                            },
                            "price": property.current_price || property.price,
                            "priceCurrency": "USD",
                            "numberOfRooms": property.bedrooms_total || property.bedrooms,
                            "propertyType": property.property_type,
                            "url": `${window.location.origin}/listing/${property.listing_key}`
                          })}
                        </script>
                        <div className="property-map">
                          <div
                            className="mini-map clickable"
                            onClick={() => setExpandedMap({...getPropertyCoords(property), address: property.address || `Property ${property.id}`})}
                          >
                            <img
                              src={`https://tile.openstreetmap.org/15/${getTileCoords(getPropertyCoords(property).lat, getPropertyCoords(property).lon, 15).x}/${getTileCoords(getPropertyCoords(property).lat, getPropertyCoords(property).lon, 15).y}.png`}
                              alt={tr(`Map tile showing location of ${property.address || 'property'}`, `Mapa con la ubicación de ${property.address || 'la propiedad'}`)}
                              className="map-tile"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                              }}
                            />
                            <div className="house-icon">🏠</div>
                            <div className="map-fallback" style={{display: 'none'}}>
                              <div className="map-dot"></div>
                            </div>
                          </div>
                        </div>
                        <div className="property-image">
                          <div
                            className="property-photo clickable"
                            onClick={() => property.isPrivate ? fetchPrivatePropertyDetail(property) : fetchPropertyDetail(property.id, property)}
                            style={{cursor: 'pointer'}}
                          >
                            <PropertyThumbnail listingKey={property.listing_key}
                                src={propertyImages[property.listing_key] || property.first_photo_url || property.image_url || property.photo_url}
                                alt={tr(`Property at ${property.address || property.street_address || 'listing'}`, `Propiedad en ${property.address || property.street_address || 'el listado'}`)}
                                className="property-thumbnail"
                              />
                          </div>
                        </div>
                        <div className="property-details">
                          <div className="property-address">{formatPropertyAddress(property) || `Property ID: ${property.id}`}</div>
                          <div className="property-price">${property.current_price || property.price}</div>
                          <div className="property-specs">
                            {(property.bedrooms_total || property.bedrooms) && `${property.bedrooms_total || property.bedrooms} ${tr('bed', 'hab.')}`}
                            {property.property_type && ` • ${property.property_type}`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {message.pageError && <p role="alert">{message.pageError}</p>}
                  {(message.hasMore || message.properties.length > ((message.currentPage || 0) + 1) * itemsPerPage) && (
                    <div style={{textAlign: 'center', marginTop: '15px'}}>
                      <button
                        className="service-button"
                        disabled={message.loadingMore}
                        onClick={() => handleShowMoreProperties(index)}
                        style={{background: '#007bff', color: 'white'}}
                      >
                        {message.loadingMore ? tr('Loading…','Cargando…') : message.restartSearch ? tr('Restart search','Reiniciar búsqueda') : tr('Show more homes','Mostrar más propiedades')}
                      </button>
                    </div>
                  )}
                </div>
              ) : message.content === 'agent_contact_capture' ? (
                message.submitted ? (
                  <p>✓ {tr('Contact details submitted.', 'Datos de contacto enviados.')}</p>
                ) : (
                <div>
                  <p>{tr('Sure! Just share your contact details and the agent will reach out:', 'Claro. Comparta sus datos de contacto y el agente se comunicará con usted:')}</p>
                  <form className="chat-form" onSubmit={async (e) => {
                    e.preventDefault();
                    setName(leadName);
                    setPhoneNumber(leadPhone);
                    if (!isHoneypotSubmission(e.currentTarget)) {
                      localStorage.setItem('leadInfo', JSON.stringify({ name: leadName, email: leadEmail, phone: leadPhone }));
                      await sendAgentSMS(leadName, leadPhone);
                    }
                    markCaptureFormSubmitted('agent_contact_capture');
                    setMessages(prev => [...prev, { type: 'bot', content: tr(`Got it, ${leadName}! The agent will contact you at ${leadPhone} shortly.`, `Entendido, ${leadName}. El agente se comunicará pronto al ${leadPhone}.`) }]);
                    setLeadName(''); setLeadPhone(''); setLeadEmail('');
                  }}>
                    <HoneypotField />
                    <input type="text" placeholder={tr('Your name', 'Su nombre')} value={leadName} onChange={(e) => setLeadName(e.target.value)} className="form-input" required />
                    <input type="tel" placeholder={tr('Phone number', 'Número de teléfono')} value={leadPhone} onChange={(e) => setLeadPhone(e.target.value)} className="form-input" required />
                    <button type="submit" className="form-submit cta-button">{tr('Contact Me', 'Contácteme')}</button>
                  </form>
                </div>
                )
              ) : message.content === 'seller_lead_capture' ? (
                message.submitted ? (
                  <p>✓ {tr('Contact details submitted.', 'Datos de contacto enviados.')}</p>
                ) : (
                <div>
                  <p>{tr('To follow up on your property, I just need a few quick details:', 'Para dar seguimiento a su propiedad, necesito algunos datos:')}</p>
                  <form className="chat-form" onSubmit={handleSellerLeadCaptureSubmit}>
                <HoneypotField />
                    <input type="text" placeholder={tr('Your name', 'Su nombre')} value={name} onChange={(e) => setName(e.target.value)} className="form-input" required />
                    <input type="tel" placeholder={tr('Phone number', 'Número de teléfono')} value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} className="form-input" required />
                    <input type="email" placeholder={tr('Email address', 'Correo electrónico')} value={email} onChange={(e) => setEmail(e.target.value)} className="form-input" required />
                    <button type="submit" className="form-submit cta-button">{tr('Submit', 'Enviar')}</button>
                  </form>
                </div>
                )
              ) : message.content === 'buyer_lead_capture' ? (
                message.submitted ? (
                  <p>✓ {tr('Contact details submitted.', 'Datos de contacto enviados.')}</p>
                ) : (
                <div>
                  <p>{tr("I'd love to help you find a home! Just need a few details:", 'Me encantaría ayudarle a encontrar una casa. Solo necesito algunos datos:')}</p>
                  <form className="chat-form" onSubmit={handleBuyerLeadCaptureSubmit}>
                <HoneypotField />
                    <input type="text" placeholder={tr('Your name', 'Su nombre')} value={name} onChange={(e) => setName(e.target.value)} className="form-input" required />
                    <input type="tel" placeholder={tr('Phone number', 'Número de teléfono')} value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} className="form-input" required />
                    <input type="email" placeholder={tr('Email address', 'Correo electrónico')} value={email} onChange={(e) => setEmail(e.target.value)} className="form-input" required />
                    {!pendingBuyerParams?.city && !pendingBuyerParams?.zip && (
                      <input type="text" placeholder={tr('City or zip code', 'Ciudad o código postal')} value={zipCode} onChange={(e) => setZipCode(e.target.value)} className="form-input" required />
                    )}
                    {!pendingBuyerParams?.maxPrice && (
                      <input type="number" placeholder={tr('Max price', 'Precio máximo')} value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="form-input" />
                    )}
                    <button type="submit" className="form-submit cta-button">{tr('Search Properties', 'Buscar propiedades')}</button>
                  </form>
                </div>
                )
              ) : (
                message.content
              )}
            </div>
          ))}
          {(isThinking || isPropertySearching) && (
            <div className="message bot-message assistant-activity-message" role="status">
              <span className="assistant-message-author">{assistantName}</span>
              <div className="assistant-activity-row">
                <span className="assistant-typing-dots"><i></i><i></i><i></i></span>
                <span>{isPropertySearching ? tr('Searching live listings and preparing your matches…', 'Buscando propiedades disponibles y preparando sus opciones…') : tr('Thinking through your request…', 'Analizando su solicitud…')}</span>
              </div>
            </div>
          )}
          {showRegistration && (
            <div className="message bot-message">
              {tr('Please complete your registration:', 'Complete su registro:')}
              <form className="chat-form" onSubmit={handleRegistrationSubmit}>
                <input
                  type="text"
                  placeholder={tr('Username', 'Nombre de usuario')}
                  value={regName}
                  onChange={(e) => setRegName(e.target.value)}
                  className="form-input"
                  required
                />
                <input
                  type="email"
                  placeholder={tr('Email', 'Correo electrónico')}
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  className="form-input"
                  required
                />
                <input
                  type="password"
                  placeholder={tr('Password', 'Contraseña')}
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  className="form-input"
                  required
                />
                <input
                  type="tel"
                  placeholder={tr('Phone Number', 'Número de teléfono')}
                  value={regPhoneNumber}
                  onChange={(e) => setRegPhoneNumber(e.target.value)}
                  className="form-input"
                  required
                />
                <input
                  type="text"
                  placeholder={tr('Full Name', 'Nombre completo')}
                  value={regFullName}
                  onChange={(e) => setRegFullName(e.target.value)}
                  className="form-input"
                  required
                />
                <button type="submit" className="form-submit">{tr('Register', 'Registrarse')}</button>
              </form>
            </div>
          )}
          {showLogin && !showForgotPassword && (
            <div className="message bot-message">
              {tr('Please sign in:', 'Inicie sesión:')}
              <form className="chat-form" onSubmit={handleLoginSubmit}>
                <input
                  type="text"
                  placeholder={tr('Username or Email', 'Usuario o correo electrónico')}
                  value={loginUsername}
                  onChange={(e) => setLoginUsername(e.target.value)}
                  className="form-input"
                  required
                />
                <input
                  type="password"
                  placeholder={tr('Password', 'Contraseña')}
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="form-input"
                  required
                />
                <button type="submit" className="form-submit">{tr('Sign In', 'Iniciar sesión')}</button>
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => setShowForgotPassword(true)}
                >
                  {tr('Forgot Password?', '¿Olvidó su contraseña?')}
                </button>
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => { setShowLogin(false); setShowRegistration(true); }}
                >
                  {tr("Don't have an account? Register", '¿No tiene una cuenta? Regístrese')}
                </button>
              </form>
            </div>
          )}
          {showForgotPassword && (
            <div className="message bot-message">
              {tr('Enter your email to reset your password:', 'Ingrese su correo para restablecer la contraseña:')}
              <form className="chat-form" onSubmit={handlePasswordResetSubmit}>
                <input
                  type="email"
                  placeholder={tr('Enter your email', 'Ingrese su correo electrónico')}
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="form-input"
                  required
                />
                <button type="submit" className="form-submit">{tr('Send Reset Link', 'Enviar enlace')}</button>
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => { setShowForgotPassword(false); setResetEmail(''); }}
                >
                  {tr('Back to Sign In', 'Volver a iniciar sesión')}
                </button>
              </form>
            </div>
          )}

        </div>
        <ChatComposer
          user={user}
          openChat={openChat}
          hidePrompts={hidePrompts}
          showPrompts={showPrompts}
          onTogglePrompts={() => setShowPrompts(!showPrompts)}
          prompts={commonPrompts}
          onPromptSelect={handlePromptSelect}
          inputValue={inputValue}
          onInputChange={setInputValue}
          onKeyPress={handleKeyPress}
          onSend={handleSendMessage}
          isBusy={isThinking || isPropertySearching}
          language={language}
          onSignIn={() => {
            setShowRegistration(false);
            setShowForgotPassword(false);
            setShowLogin(true);
          }}
        />
          </section>
          {hasPropertyWorkspace && (
            <aside className="assistant-results-panel" ref={assistantResultsPanelRef} aria-label={`${latestPropertyMessage.properties.length} matching properties`}>
              <button type="button" className="assistant-mobile-back-to-chat" onClick={() => scrollMobileWorkspaceTo('chat')}>
                <span aria-hidden="true">←</span>
                {tr('Back to chat', 'Volver al chat')}
              </button>
              <div className="assistant-results-header">
                <div>
                  <span className="assistant-results-eyebrow">{tr('Live search results', 'Resultados actualizados')}</span>
                  <h2>{latestPropertyMessage.properties.length} {tr('properties', 'propiedades')} <em>{tr('worth exploring', 'que vale la pena explorar')}</em></h2>
                  <p>{tr('Select a home to see every detail without losing your conversation.', 'Seleccione una propiedad para ver todos los detalles sin perder la conversación.')}</p>
                </div>
                <div className="assistant-results-actions">
                  <button type="button" onClick={() => handlePromptSelect(tr('Refine these property results', 'Ayúdame a refinar estos resultados'))}>{tr('Refine search', 'Refinar búsqueda')}</button>
                  <button type="button" className="assistant-results-alert" onClick={() => handlePromptSelect(tr('Create a property alert for this search', 'Crea una alerta para esta búsqueda'))}>{tr('Create alert', 'Crear alerta')}</button>
                </div>
              </div>
              <div className="assistant-property-grid" ref={assistantPropertyRailRef} key={latestPropertyMessage.searchId} aria-label="Matching homes">
                {visibleWorkspaceProperties.map((property, propertyIndex) => {
                  const propertyKey = getPropertyKey(property, propertyIndex);
                  const propertyAddress = getPropertyAddress(property);
                  const propertyPhoto = propertyImages[property.listing_key] || property.first_photo_url || property.image_url || property.photo_url;
                  const rawPropertyLatitude = property.latitude ?? property.location?.latitude;
                  const rawPropertyLongitude = property.longitude ?? property.location?.longitude;
                  const propertyLatitude = Number(rawPropertyLatitude);
                  const propertyLongitude = Number(rawPropertyLongitude);
                  const hasPropertyCoords = rawPropertyLatitude != null && rawPropertyLatitude !== '' && rawPropertyLongitude != null && rawPropertyLongitude !== '' && Number.isFinite(propertyLatitude) && Number.isFinite(propertyLongitude);
                  const propertyMapTile = hasPropertyCoords ? getTileCoords(propertyLatitude, propertyLongitude, 15) : null;
                  const bedrooms = property.bedrooms_total || property.bedrooms;
                  const bathrooms = property.bathrooms_total_integer || property.bathrooms_total || property.bathrooms;
                  const squareFeet = property.living_area || property.building_area_total || property.square_feet;
                  const isSaved = likedProperties.has(propertyKey);
                  return (
                    <article key={propertyKey} data-property-index={propertyIndex} className={`assistant-property-card ${hasPropertyCoords ? 'assistant-property-card-has-map' : ''}`}>
                      <button
                        type="button"
                        className="assistant-property-media"
                        onClick={() => property.isPrivate ? fetchPrivatePropertyDetail(property) : fetchPropertyDetail(property.id, property)}
                        aria-label={tr(`View details for ${propertyAddress}`, `Ver detalles de ${propertyAddress}`)}
                      >
                        <PropertyThumbnail listingKey={property.listing_key} src={propertyPhoto} alt={`Property at ${propertyAddress}`} />
                        <span className="assistant-property-live">{tr('Live listing', 'Propiedad disponible')}</span>
                      </button>
                      {hasPropertyCoords && (
                        <button
                          type="button"
                          className="assistant-property-map-thumbnail"
                          onClick={() => setExpandedMap({ lat: propertyLatitude, lon: propertyLongitude, address: propertyAddress })}
                          aria-label={tr(`Open interactive map for ${propertyAddress}`, `Abrir el mapa interactivo de ${propertyAddress}`)}
                          title={tr('Open interactive map', 'Abrir mapa interactivo')}
                        >
                          <img
                            src={`https://tile.openstreetmap.org/15/${propertyMapTile.x}/${propertyMapTile.y}.png`}
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                          />
                          <span aria-hidden="true"><i /></span>
                          <small>{tr('Map', 'Mapa')}</small>
                        </button>
                      )}
                      <button
                        type="button"
                        className={`assistant-property-save ${isSaved ? 'saved' : ''}`}
                        onClick={() => toggleWorkspaceFavorite(property, propertyIndex)}
                        aria-label={isSaved ? tr(`Remove ${propertyAddress} from saved properties`, `Quitar ${propertyAddress} de las propiedades guardadas`) : tr(`Save ${propertyAddress}`, `Guardar ${propertyAddress}`)}
                      >
                        {isSaved ? tr('Saved', 'Guardada') : tr('Save', 'Guardar')}
                      </button>
                      <div className="assistant-property-content">
                        <div className="assistant-property-price-row">
                          <strong>{formatPropertyPrice(property.current_price || property.price)}</strong>
                          {property.property_type && <span>{property.property_type}</span>}
                        </div>
                        <p>{propertyAddress}</p>
                        <div className="assistant-property-specs">
                          {bedrooms && <span><strong>{bedrooms}</strong> {tr('beds', 'hab.')}</span>}
                          {bathrooms && <span><strong>{bathrooms}</strong> {tr('baths', 'baños')}</span>}
                          {squareFeet && <span><strong>{Number.isFinite(Number(squareFeet)) ? Number(squareFeet).toLocaleString() : squareFeet}</strong> {tr('sq ft', 'pies²')}</span>}
                        </div>
                        <div className="assistant-property-insight">
                          <span></span>
                          {tr('A current match from the live Atlanta-area feed.', 'Una opción actual del listado del área de Atlanta.')}
                        </div>
                        <div className="assistant-property-actions">
                          <button type="button" onClick={() => property.isPrivate ? fetchPrivatePropertyDetail(property) : fetchPropertyDetail(property.id, property)}>{tr('View residence', 'Ver propiedad')}</button>
                          <button type="button" onClick={() => handlePromptSelect(tr(`I would like to schedule a showing for ${propertyAddress}`, `Quiero programar una visita a ${propertyAddress}`))}>{tr('Schedule showing', 'Programar visita')}</button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              {remainingWorkspaceProperties > 0 && (
                <div className="assistant-results-load-more" aria-label="More matching homes" aria-busy={Boolean(latestPropertyMessage.loadingMore)}>
                  <div className="assistant-results-progress-copy">
                    <span>{visibleWorkspaceProperties.length} homes shown</span>
                    <span>{latestPropertyMessage.hasMore?'More homes available':`${remainingWorkspaceProperties} remaining`}</span>
                  </div>

                  {latestPropertyMessage.pageError && <p className="assistant-results-page-error" role="alert">{latestPropertyMessage.pageError}</p>}
                  <button type="button" className="assistant-results-more" disabled={latestPropertyMessage.loadingMore} onClick={latestPropertyMessage.restartSearch ? () => fetchProperties(latestPropertyMessage.searchFilters) : handleWorkspaceShowMore}>
                    <span className="assistant-results-more-copy">
                      <small>Continue browsing</small>
                      <strong>{latestPropertyMessage.loadingMore ? 'Loading homes…' : latestPropertyMessage.restartSearch ? 'Restart search' : latestPropertyMessage.pageError ? 'Try again' : `See ${nextWorkspacePropertyCount} more properties`}</strong>
                    </span>
                    <span className="assistant-results-more-arrow" aria-hidden="true">→</span>
                  </button>
                </div>
              )}
              </div>
              <button type="button" className="assistant-results-chat-cta" onClick={() => scrollMobileWorkspaceTo('chat')}>
                <span>Ask about these properties</span>
                <small>Return to your conversation</small>
              </button>
            </aside>
          )}
        </div>
      </div>
      {hasPropertyWorkspace && latestPropertyMessage.searchId != null && (
        <PropertySwipeHint key={`${latestPropertyMessageIndex}:${latestPropertyMessage.searchId}`} workspaceRef={assistantWorkspaceRef} resultsRef={assistantResultsPanelRef} paused={isPropertySearching || Boolean(propertyDetail)} />
      )}
      {expandedMap && (
        <div className="expanded-map-modal" onClick={() => setExpandedMap(null)}>
          <div className="expanded-map" onClick={(e) => e.stopPropagation()}>
            <button className="close-expanded-map" onClick={() => setExpandedMap(null)}>
              ×
            </button>
            <div ref={mapRef} className="leaflet-map"></div>
              <MapPlaceLayers map={placesExpandedMapInstance} />
          </div>
        </div>
      )}
      {propertyDetailOverlay}
    </div>
  );
};

export default ActionButtons;
